import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import { motion, AnimatePresence } from 'framer-motion';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { getManagerDashboardStats, formatApiError, exportManagerDashboardData, getImputations, getTenantSubdomain } from '../services/api';
import { getCompletedEtatCode, RS_ETAT_CODES } from '../utils/imputationEtats';
import {
  RS_CHART_BASE,
  RS_CHART_SERIES,
  RS_CHART_GROUP,
  RS_CHART_OPERATION_NESTED,
  RS_CHART_STATUS_LINE,
  RS_CHART_DUAL_BAR,
  rsLineAreaGradient,
} from '../utils/rsChartPalette';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import OccupationRatesChartCard from './OccupationRatesChartCard';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Weekday filter uses JS Date.getDay() values: 0=Sunday … 6=Saturday. Display order Mon→Sun (M T W T F S S).
const WEEKDAY_TOGGLE_CONFIG = [
  { letter: 'M', day: 1, labelKey: 'weekdays.monday' },
  { letter: 'T', day: 2, labelKey: 'weekdays.tuesday' },
  { letter: 'W', day: 3, labelKey: 'weekdays.wednesday' },
  { letter: 'T', day: 4, labelKey: 'weekdays.thursday' },
  { letter: 'F', day: 5, labelKey: 'weekdays.friday' },
  { letter: 'S', day: 6, labelKey: 'weekdays.saturday' },
  { letter: 'S', day: 0, labelKey: 'weekdays.sunday' },
];

const DEFAULT_MANAGER_DASHBOARD_WEEKDAYS = [1, 2, 3, 4, 5]; // Mon–Fri (JS Date.getDay)

const ManagerDashboardView = () => {
  const { t, i18n } = useTranslation('manager');
  const { t: tCommon } = useTranslation('common');
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const chartTextColor = isDark ? '#94a3b8' : '#617289';
  const chartGridColor = isDark ? '#334155' : '#f0f2f4';
  const chartTooltipBg = isDark ? '#1e293b' : '#111418';
  const isRsDashboard = (getTenantSubdomain() || '').toLowerCase() === 'rs';
  const topOpsAxisPrimary = isRsDashboard ? RS_CHART_DUAL_BAR.primary : '#1162d4';
  const topOpsAxisSecondary = isRsDashboard ? RS_CHART_DUAL_BAR.secondary : '#60a5fa';
  const statusBreakdownLegendMap = isRsDashboard
    ? {
        TO_DO: { label: t('statuses.toDo'), color: '#1a1a1a' },
        QUALIFIED: { label: t('statuses.qualified'), color: '#232323' },
        IN_PROGRESS: { label: t('statuses.inProgress'), color: '#2a2a2a' },
        SOURCED: { label: t('statuses.sourced'), color: '#3d3d3d' },
        TO_PROPOS: { label: t('statuses.toPropos'), color: '#474747' },
        PROPOSED: { label: t('statuses.proposed'), color: '#4f4f4f' },
        PRESENTED: { label: t('statuses.presented'), color: '#616161' },
        FINAL: { label: t('statuses.final'), color: '#737373' },
        COMPLETED: { label: t('statuses.completed'), color: '#858585' },
      }
    : {
        TERMINE: { label: t('statuses.termine'), color: '#10b981' },
        ECHEC: { label: t('statuses.echec'), color: '#ef4444' },
      };
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    period: 'Last 30 Days',
    date_from: '',
    date_to: '',
    centre: '',
    team: '',
    employee: '',
    domain: [], // multiple selection: array of domain names
    operation: '',
    includeIncompleteOperations: false,
    showAllStatuses: false,
    production_line_lp: '',
    program: '',
    project_type: '',
    weekdays: [...DEFAULT_MANAGER_DASHBOARD_WEEKDAYS],
  });
  
  // Chart view option for Executed Operations Overview
  const [executedOpsViewByDomain, setExecutedOpsViewByDomain] = useState(false);
  // Metric selection for Executed Operations Overview (operations count vs standard hours)
  const [operationsMetric, setOperationsMetric] = useState('count'); // 'count' | 'standard'
  
  // Distribution chart mode: by Domain or by Production Type
  const [distributionChartMode, setDistributionChartMode] = useState('domain'); // 'domain' | 'production_type'
  
  // Toggle for showing all statuses separately vs grouped incomplete (only for linear chart)
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  
  // Employee dropdown state
  const [employeeDropdownOpen, setEmployeeDropdownOpen] = useState(false);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  
  // Domain dropdown state (multi-select, input with options)
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState('');
  const domainDropdownRef = useRef(null);

  // Close Domain dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (domainDropdownOpen && domainDropdownRef.current && !domainDropdownRef.current.contains(e.target)) {
        setDomainDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [domainDropdownOpen]);

  // LP (Production Line) dropdown state
  const [lpDropdownOpen, setLpDropdownOpen] = useState(false);
  const [lpSearchQuery, setLpSearchQuery] = useState('');
  const lpDropdownRef = useRef(null);

  // Close LP dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (lpDropdownOpen && lpDropdownRef.current && !lpDropdownRef.current.contains(e.target)) {
        setLpDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [lpDropdownOpen]);

  // Program dropdown state
  const [programDropdownOpen, setProgramDropdownOpen] = useState(false);
  const [programSearchQuery, setProgramSearchQuery] = useState('');
  const programDropdownRef = useRef(null);

  // Project Type dropdown state
  const [projectTypeDropdownOpen, setProjectTypeDropdownOpen] = useState(false);
  const [projectTypeSearchQuery, setProjectTypeSearchQuery] = useState('');
  const projectTypeDropdownRef = useRef(null);

  // Close Program dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (programDropdownOpen && programDropdownRef.current && !programDropdownRef.current.contains(e.target)) {
        setProgramDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [programDropdownOpen]);

  // Close Project Type dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (projectTypeDropdownOpen && projectTypeDropdownRef.current && !projectTypeDropdownRef.current.contains(e.target)) {
        setProjectTypeDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [projectTypeDropdownOpen]);

  // Operation dropdown state
  const [operationDropdownOpen, setOperationDropdownOpen] = useState(false);
  const [operationSearchQuery, setOperationSearchQuery] = useState('');
  const [operationsList, setOperationsList] = useState([]);
  const [loadingOperations, setLoadingOperations] = useState(false);
  
  // Debounce timer
  const [debounceTimer, setDebounceTimer] = useState(null);
  
  // State for completed operations count (for efficiency rate calculation)
  const [completedOperationsCount, setCompletedOperationsCount] = useState(null);
  
  // State for tracking copied items (for visual feedback)
  const [copiedItem, setCopiedItem] = useState(null);
  
  // State for export in progress (shows "Preparing export..." message)
  const [exporting, setExporting] = useState(false);
  
  // Saved filters state
  const [savedFilters, setSavedFilters] = useState([]);
  const [saveFilterDialogOpen, setSaveFilterDialogOpen] = useState(false);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [savedFiltersAccordionOpen, setSavedFiltersAccordionOpen] = useState(false);
  
  // Copy to clipboard function
  const handleCopy = async (text, itemId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedItem(itemId);
      setTimeout(() => setCopiedItem(null), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  // Fetch dashboard data
  const fetchDashboardData = useCallback(async () => {
    // #region agent log
    // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:28',message:'fetchDashboardData called',data:{filters},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    setLoading(true);
    setError(null);
    try {
      const data = await getManagerDashboardStats(filters);
      // #region agent log
      // fetch('http://127.0.0.1:7248/ingest/125347bc-248b-4be8-92f0-9f8335a126cc',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:fetchDashboardData success',message:'getManagerDashboardStats returned',data:{hasData:!!data,dataKeys:data?Object.keys(data):[]},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'H2,H4'})}).catch(()=>{});
      // #endregion
      setDashboardData(data);
      setError(null); // Clear any stale error when we have data so UI does not show "Failed to load dashboard data"
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:35',message:'dashboardData state set',data:{hasData:!!data},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
    } catch (err) {
      // #region agent log
      const formattedError = formatApiError(err);
      // fetch('http://127.0.0.1:7248/ingest/125347bc-248b-4be8-92f0-9f8335a126cc',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:fetchDashboardData catch',message:'fetchDashboardData error',data:{errStatus:err?.status,errDataKeys:err?.data?Object.keys(err.data):[],errMessage:err?.message,generalError:formattedError?.generalError,fieldErrorKeys:formattedError?.fieldErrors?Object.keys(formattedError.fieldErrors):[],showingFallback:!formattedError?.generalError},timestamp:Date.now(),sessionId:'debug-session',hypothesisId:'H1,H3,H5'})}).catch(()=>{});
      // #endregion
      setError(formattedError.generalError || t('loadFailedData'));
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }, [filters]);
  
  // Helper function to convert period to date range (matching backend logic)
  const getDateRangeFromPeriod = (period, dateFrom, dateTo) => {
    // If custom dates are provided, use them
    if (dateFrom && dateTo) {
      return { date_from: dateFrom, date_to: dateTo };
    }
    
    // Convert period to date range
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    let date_from, date_to;
    
    switch (period) {
      case 'Today':
        date_from = formatDate(today);
        date_to = formatDate(today);
        break;
      case 'Yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        date_from = formatDate(yesterday);
        date_to = formatDate(yesterday);
        break;
      case 'Last 7 Days':
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        date_from = formatDate(sevenDaysAgo);
        date_to = formatDate(today);
        break;
      case 'Last 30 Days':
        const thirtyDaysAgo = new Date(today);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        date_from = formatDate(thirtyDaysAgo);
        date_to = formatDate(today);
        break;
      case 'This Quarter':
        const quarterStartMonth = Math.floor((today.getMonth()) / 3) * 3;
        const quarterStart = new Date(today.getFullYear(), quarterStartMonth, 1);
        date_from = formatDate(quarterStart);
        date_to = formatDate(today);
        break;
      case 'YTD':
        const yearStart = new Date(today.getFullYear(), 0, 1);
        date_from = formatDate(yearStart);
        date_to = formatDate(today);
        break;
      default:
        // Default to Last 30 Days
        const defaultStart = new Date(today);
        defaultStart.setDate(defaultStart.getDate() - 30);
        date_from = formatDate(defaultStart);
        date_to = formatDate(today);
    }
    
    return { date_from, date_to };
  };
  
  // Fetch completed operations count for efficiency rate calculation
  // Note: This uses getImputations which may not support all dashboard filters (centre, team, employee, domain)
  // For accurate calculation, the backend should provide completed_operations count in dashboard stats
  const fetchCompletedOperationsCount = useCallback(async () => {
    // #region agent log
    // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:fetchCompletedOperationsCount',message:'fetchCompletedOperationsCount called',data:{filters,period:filters.period,date_from:filters.date_from,date_to:filters.date_to,operation:filters.operation},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    try {
      // Convert period to date range if needed
      const dateRange = getDateRangeFromPeriod(filters.period, filters.date_from, filters.date_to);
      
      // Build filter params - only use filters supported by getImputations API
      const filterParams = {
        date_from: dateRange.date_from,
        date_to: dateRange.date_to,
        operation: filters.operation || undefined,
        etat_final: getCompletedEtatCode(getTenantSubdomain()), // Completed-only: TERMINE or COMPLETED (RS)
      };
      
      // Remove undefined values
      Object.keys(filterParams).forEach(key => 
        filterParams[key] === undefined && delete filterParams[key]
      );
      
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:fetchCompletedOperationsCount',message:'filterParams prepared',data:{filterParams,dateRange,hasDateFrom:!!filterParams.date_from,hasDateTo:!!filterParams.date_to,hasOperation:!!filterParams.operation},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      
      // Fetch completed operations count
      // Note: This may not match total_operations exactly if centre/team/employee/domain filters are applied
      // because getImputations doesn't support those filters
      const response = await getImputations(filterParams, 1, 1); // Just get count, minimal data
      
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:fetchCompletedOperationsCount',message:'getImputations response received',data:{hasResponse:!!response,responseKeys:response?Object.keys(response):[],count:response?.count,countType:typeof response?.count},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      // #endregion
      
      const count = response?.count || 0;
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:fetchCompletedOperationsCount',message:'setting completedOperationsCount',data:{count,countValue:count},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
      // #endregion
      setCompletedOperationsCount(count);
    } catch (error) {
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:fetchCompletedOperationsCount',message:'error caught',data:{errorMessage:error?.message,errorStatus:error?.status,errorStack:error?.stack?.substring(0,200)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      // #endregion
      console.error('Error fetching completed operations count:', error);
      setCompletedOperationsCount(null);
    }
  }, [filters]);
  
  // Debounced filter change handler
  useEffect(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
    
    const timer = setTimeout(() => {
      fetchDashboardData();
      fetchCompletedOperationsCount();
    }, 500); // 500ms debounce
    
    setDebounceTimer(timer);
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [filters, fetchDashboardData, fetchCompletedOperationsCount]);
  
  // Load saved filters from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem('managerDashboardSavedFilters');
      if (saved) {
        const parsed = JSON.parse(saved);
        setSavedFilters(Array.isArray(parsed) ? parsed : []);
      }
    } catch (error) {
      console.error('Error loading saved filters:', error);
      setSavedFilters([]);
    }
  }, []); // Only on mount
  
  
  // Helper function to get filter display value
  const getFilterDisplayValue = (filterKey, filterValue, filterOptions, opsList = []) => {
    if (!filterValue) return t('all');
    
    switch (filterKey) {
      case 'period': {
        const periodMap = {
          'Today': t('periods.today'),
          'Yesterday': t('periods.yesterday'),
          'Last 7 Days': t('periods.last7Days'),
          'Last 30 Days': t('periods.last30Days'),
          'This Quarter': t('periods.thisQuarter'),
          'Custom Range': t('periods.customRange'),
          'YTD': t('periods.ytd'),
        };
        return periodMap[filterValue] || filterValue;
      }
      case 'centre':
        const centre = filterOptions?.centres?.find(c => c.id.toString() === filterValue.toString());
        return centre ? centre.name : filterValue;
      case 'team':
        const team = filterOptions?.teams?.find(t => t.id.toString() === filterValue.toString());
        return team ? team.name : filterValue;
      case 'employee':
        const employee = filterOptions?.employees?.find(e => e.id.toString() === filterValue.toString());
        return employee ? (employee.name || employee.email) : filterValue;
      case 'domain':
        return filterValue;
      case 'operation':
        const op = opsList.find(o => o.id.toString() === filterValue.toString());
        return op ? [op.production_line_lp, op.activity].filter(Boolean).join(' - ') || String(filterValue) : String(filterValue);
      case 'date_from':
      case 'date_to':
        return filterValue ? new Date(filterValue).toLocaleDateString(getDateLocale(i18n.language)) : t('notSet');
      case 'includeIncompleteOperations':
      case 'showAllStatuses':
        return filterValue ? tCommon('yes') : tCommon('no');
      default:
        return filterValue || tCommon('all');
    }
  };
  
  // Initial fetch
  useEffect(() => {
    fetchDashboardData();
    fetchCompletedOperationsCount();
  }, []); // Only on mount
  
  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      period: 'Last 30 Days',
      date_from: '',
      date_to: '',
      centre: '',
      team: '',
      employee: '',
      domain: [],
      operation: '',
      includeIncompleteOperations: false,
      showAllStatuses: false,
      production_line_lp: '',
      program: '',
      project_type: '',
      weekdays: [...DEFAULT_MANAGER_DASHBOARD_WEEKDAYS],
    });
    setEmployeeSearchQuery('');
    setOperationSearchQuery('');
    setLpSearchQuery('');
    setProgramSearchQuery('');
    setProjectTypeSearchQuery('');
    setDomainSearchQuery('');
    setEmployeeDropdownOpen(false);
    setOperationDropdownOpen(false);
    setLpDropdownOpen(false);
    setProgramDropdownOpen(false);
    setProjectTypeDropdownOpen(false);
    setDomainDropdownOpen(false);
  };
  
  // Save current filters
  const handleSaveFilter = () => {
    if (!saveFilterName.trim()) {
      alert(t('enterFilterName'));
      return;
    }
    
    const newFilter = {
      id: Date.now().toString(),
      name: saveFilterName.trim(),
      filters: { ...filters },
      createdAt: new Date().toISOString(),
    };
    
    const updated = [...savedFilters, newFilter];
    setSavedFilters(updated);
    
    try {
      localStorage.setItem('managerDashboardSavedFilters', JSON.stringify(updated));
      setSaveFilterDialogOpen(false);
      setSaveFilterName('');
      // alert(`Filter "${newFilter.name}" saved successfully!`);
    } catch (error) {
      console.error('Error saving filter:', error);
      alert(t('saveFilterFailed'));
    }
  };
  
  // Load a saved filter
  const handleLoadFilter = (savedFilter) => {
    const loaded = { ...savedFilter.filters };
    // Normalize domain to array (saved filters may have string from older saves)
    if (typeof loaded.domain === 'string') {
      loaded.domain = loaded.domain ? [loaded.domain] : [];
    } else if (!Array.isArray(loaded.domain)) {
      loaded.domain = [];
    }
    if (!Array.isArray(loaded.weekdays) || loaded.weekdays.length === 0) {
      loaded.weekdays = [...DEFAULT_MANAGER_DASHBOARD_WEEKDAYS];
    }
    setFilters(loaded);
    setEmployeeSearchQuery('');
    setOperationSearchQuery('');
    setEmployeeDropdownOpen(false);
    setOperationDropdownOpen(false);
    setDomainDropdownOpen(false);
  };
  
  // Delete a saved filter
  const handleDeleteFilter = (filterId, e) => {
    e.stopPropagation(); // Prevent triggering load when clicking delete
    if (window.confirm(t('confirmDeleteFilter'))) {
      const updated = savedFilters.filter(f => f.id !== filterId);
      setSavedFilters(updated);
      
      try {
        localStorage.setItem('managerDashboardSavedFilters', JSON.stringify(updated));
      } catch (error) {
        console.error('Error deleting filter:', error);
        alert(t('deleteFilterFailed'));
      }
    }
  };
  
  // Refresh dashboard data
  const handleRefreshData = () => {
    fetchDashboardData();
    fetchCompletedOperationsCount();
  };
  
  const isWeekdayActive = (day) => (filters.weekdays || []).includes(day);

  const toggleWeekday = (day) => {
    setFilters((prev) => {
      const cur = [...(prev.weekdays || [])];
      const i = cur.indexOf(day);
      if (i >= 0) {
        cur.splice(i, 1);
      } else {
        cur.push(day);
        cur.sort((a, b) => a - b);
      }
      return { ...prev, weekdays: cur };
    });
  };

  // Handle filter changes
  const handleFilterChange = (filterName, value) => {
    // #region agent log
    // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:107',message:'handleFilterChange called',data:{filterName,value,currentPeriod:filters.period},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    setFilters(prev => {
      const newFilters = { ...prev };
      
      // Handle period changes
      if (filterName === 'period') {
        if (value === 'Custom Range') {
          // When Custom Range is selected, set period to "Custom Range"
          newFilters.period = 'Custom Range';
          // Keep existing dates if they exist, otherwise leave empty for user to set
        } else {
          // When predefined period is selected, clear custom dates
          newFilters.period = value;
          newFilters.date_from = '';
          newFilters.date_to = '';
        }
        // #region agent log
        // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:119',message:'period filter changed',data:{newPeriod:newFilters.period,value,isThisQuarter:value==='This Quarter'},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
        // #endregion
      } else {
        // For other filters, just update the value
        newFilters[filterName] = value;
      }
      
      return newFilters;
    });
  };
  
  // Get selected employee display name
  const getSelectedEmployeeName = () => {
    if (!filters.employee) return '';
    const selectedEmployee = dashboardData?.filter_options?.employees?.find(
      e => e.id.toString() === filters.employee.toString()
    );
    if (selectedEmployee) {
      return `${selectedEmployee.name} (${selectedEmployee.email})`;
    }
    return '';
  };
  
  // Filter employees based on search query
  const filteredEmployees = dashboardData?.filter_options?.employees?.filter(employee => {
    if (!employeeSearchQuery) return true;
    const searchLower = employeeSearchQuery.toLowerCase();
    const name = (employee.name || '').toLowerCase();
    const email = (employee.email || '').toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower);
  }) || [];
  
  // Fetch operations list
  const fetchOperationsList = useCallback(async () => {
    setLoadingOperations(true);
    try {
      const response = await api.getOperations({ status: 'active' }, 1, 1000); // Large page size to get all
      const operationsArray = response.results || [];
      setOperationsList(operationsArray);
    } catch (error) {
      console.error('Error fetching operations:', error);
    } finally {
      setLoadingOperations(false);
    }
  }, []);
  
  // Fetch operations on mount
  useEffect(() => {
    fetchOperationsList();
  }, [fetchOperationsList]);
  
  // Get selected operation display name
  const getSelectedOperationName = () => {
    if (!filters.operation) return '';
    const selectedOperation = operationsList.find(
      op => op.id.toString() === filters.operation.toString()
    );
    if (selectedOperation) {
      return `${selectedOperation.production_line_lp} - ${selectedOperation.activity}`;
    }
    return '';
  };
  
  // Filter operations based on search query
  const filteredOperations = operationsList.filter(operation => {
    if (!operationSearchQuery) return true;
    const searchLower = operationSearchQuery.toLowerCase();
    const code = (operation.production_line_lp || '').toLowerCase();
    const name = (operation.activity || '').toLowerCase();
    return code.includes(searchLower) || name.includes(searchLower);
  });
  
  // Handle export
  const handleExport = async (format) => {
    // #region agent log
    // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:126',message:'handleExport called',data:{format,filters},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
    // #endregion
    setExporting(true);
    try {
      // Determine the export format (excel or csv)
      const exportFormat = format === 'excel' ? 'excel' : 'csv';
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:130',message:'exportFormat determined',data:{exportFormat},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      // #endregion
      
      // Call export API
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:133',message:'calling exportManagerDashboardData',data:{filters,exportFormat},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
      // #endregion
      const blob = await exportManagerDashboardData(filters, exportFormat);
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:136',message:'exportManagerDashboardData returned',data:{hasBlob:!!blob,blobSize:blob?.size,blobType:blob?.type},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      // #endregion
      
      // Determine file extension and MIME type
      const extension = exportFormat === 'excel' ? 'xlsx' : 'csv';
      const mimeType = exportFormat === 'excel' 
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv';
      
      // Create blob URL (use blob directly - it already has the correct type from server)
      const blobUrl = window.URL.createObjectURL(blob);
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:144',message:'blob URL created',data:{blobUrl,extension,mimeType},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      // #endregion
      
      // Create temporary download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `imputations_${new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5)}.${extension}`;
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:153',message:'download link clicked',data:{filename:link.download},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'E'})}).catch(()=>{});
      // #endregion
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:158',message:'handleExport error caught',data:{errorMessage:error?.message,errorStatus:error?.status,errorData:error?.data,errorStack:error?.stack?.substring(0,200)},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
      // #endregion
      console.error('Export failed:', error);
      const formattedError = formatApiError(error);
      alert(formattedError.generalError || t('exportFailed'));
    } finally {
      setExporting(false);
    }
  };
  
  // Prepare nested donut chart data: by domain or by production type (inner = groups, outer = operations)
  const getDistributionChartData = (distribution, labelKey) => {
    if (!distribution || distribution.length === 0) {
      return null;
    }
    const getLabel = (item) => (item[labelKey] != null && item[labelKey] !== '') ? item[labelKey] : t('other');
    const top_operations = dashboardData?.top_operations || [];
    const hasOperationsInDistribution = distribution?.some(d => d.operations && d.operations.length > 0);
    const useTopOpsFallback = labelKey === 'domain' && !hasOperationsInDistribution && top_operations.length > 0;

    if (!hasOperationsInDistribution && !useTopOpsFallback) {
      const groupColors = isRsDashboard ? RS_CHART_GROUP : ['#1162d4', '#60a5fa', '#93c5fd', '#e2e8f0', '#fbbf24', '#f59e0b'];
      return {
        innerLabels: distribution.map(item => getLabel(item)),
        innerData: distribution.map(item => item.count || 0),
        innerColors: distribution.map((_, index) => groupColors[index % groupColors.length]),
        innerMetadata: distribution.map(item => ({
          domain: getLabel(item),
          count: item.count || 0,
          completed_count: item.completed_count !== undefined ? item.completed_count : item.count || 0,
          incomplete_count: item.incomplete_count !== undefined ? item.incomplete_count : 0,
          percentage: item.percentage || 0,
        })),
        outerLabels: [],
        outerData: [],
        outerColors: [],
        outerMetadata: [],
        operationMap: {},
      };
    }

    const operationsByGroup = {};
    
    if (hasOperationsInDistribution) {
      distribution.forEach((groupItem) => {
        const groupLabel = getLabel(groupItem);
        if (groupItem.operations && groupItem.operations.length > 0) {
          operationsByGroup[groupLabel] = groupItem.operations.map(op => {
            const totalCount = op.total_count !== undefined && op.total_count !== null
              ? op.total_count
              : ((op.completed_count || 0) + (op.incomplete_count || 0));
            return {
              id: op.id,
              name: op.activity || t('unknown'),
              production_line_lp: op.production_line_lp,
              count: totalCount,
              total_count: totalCount,
              completed_count: op.completed_count !== undefined ? op.completed_count : 0,
              incomplete_count: op.incomplete_count !== undefined ? op.incomplete_count : 0,
            };
          });
        }
      });
    } else if (useTopOpsFallback) {
      top_operations.forEach(op => {
        const groupLabel = op.domain || t('other');
        if (!operationsByGroup[groupLabel]) operationsByGroup[groupLabel] = [];
        const opCount = op.count || 0;
        operationsByGroup[groupLabel].push({
          id: op.id,
          name: op.activity || t('unknown'),
          production_line_lp: op.production_line_lp,
          count: opCount,
          total_count: opCount,
          completed_count: opCount,
          incomplete_count: 0,
        });
      });
    }

    const sortedGroups = [...distribution].sort((a, b) => (b.count || 0) - (a.count || 0));
    const groupColors = isRsDashboard ? RS_CHART_GROUP : ['#1162d4', '#60a5fa', '#93c5fd', '#e2e8f0', '#fbbf24', '#f59e0b'];
    const operationColors = isRsDashboard
      ? RS_CHART_OPERATION_NESTED
      : [
          '#1162d4', '#0d4fa3', '#1a75f5', '#60a5fa', '#3d8cf7', '#83b3fc',
          '#93c5fd', '#6eb2fc', '#b8d9fe', '#cbd5e1', '#a8b5c8', '#e8ecf2',
        ];

    const innerLabels = sortedGroups.map(item => getLabel(item));
    const innerData = sortedGroups.map(item => item.count || 0);
    const innerColors = sortedGroups.map((_, index) => groupColors[index % groupColors.length]);
    const outerLabels = [];
    const outerData = [];
    const outerColors = [];
    const operationMap = {};
    const outerMetadata = [];

    const getOperationTotalCount = (op) => {
      if (op.total_count !== undefined && op.total_count !== null) return op.total_count;
      if (op.completed_count !== undefined && op.incomplete_count !== undefined) return (op.completed_count || 0) + (op.incomplete_count || 0);
      return op.count || 0;
    };

    sortedGroups.forEach((groupItem, groupIndex) => {
      const groupLabel = getLabel(groupItem);
      const groupOps = operationsByGroup[groupLabel] || [];
      const topOpsInGroup = groupOps
        .sort((a, b) => getOperationTotalCount(b) - getOperationTotalCount(a))
        .slice(0, 3);

      topOpsInGroup.forEach((op, opIndex) => {
        const colorIndex = (groupIndex * 3 + opIndex) % operationColors.length;
        const opColor = operationColors[colorIndex];
        const operationName = op.activity || t('unknown');
        const operationCount = getOperationTotalCount(op);
        outerLabels.push(operationName);
        outerData.push(operationCount);
        outerColors.push(opColor);
        outerMetadata.push({
          domain: groupLabel,
          operationName,
          count: operationCount,
          completed_count: op.completed_count,
          incomplete_count: op.incomplete_count,
        });
        if (!operationMap[groupLabel]) operationMap[groupLabel] = [];
        operationMap[groupLabel].push({
          id: op.id,
          name: op.activity,
          production_line_lp: op.production_line_lp,
          count: operationCount,
          completed_count: op.completed_count !== undefined ? op.completed_count : op.count,
          incomplete_count: op.incomplete_count !== undefined ? op.incomplete_count : 0,
          color: opColor,
        });
      });
      if (topOpsInGroup.length === 0) {
        outerLabels.push(groupLabel);
        outerData.push(groupItem.count || 0);
        outerColors.push(groupColors[groupIndex % groupColors.length]);
        outerMetadata.push({ domain: groupLabel, operationName: groupLabel, count: groupItem.count || 0 });
      }
    });

    const innerMetadata = sortedGroups.map((item) => {
      const source = distribution.find(d => getLabel(d) === getLabel(item)) || item;
      return {
        domain: getLabel(item),
        count: item.count || 0,
        completed_count: source.completed_count !== undefined ? source.completed_count : item.count || 0,
        incomplete_count: source.incomplete_count !== undefined ? source.incomplete_count : 0,
        percentage: item.percentage || 0,
        status_breakdown: source.status_breakdown || null,
      };
    });

    return {
      innerLabels,
      innerData,
      innerColors,
      innerMetadata,
      outerLabels,
      outerData,
      outerColors,
      outerMetadata,
      operationMap,
    };
  };

  // Legacy domain chart data (for backward compatibility if needed)
  const getDomainChartData = () => {
    if (!dashboardData?.domain_distribution || dashboardData.domain_distribution.length === 0) {
      return { segments: [], total: 0 };
    }
    
    const total = dashboardData.domain_distribution.reduce((sum, item) => sum + item.count, 0);
    let currentPercent = 0;
    const segments = dashboardData.domain_distribution.map((item, index) => {
      const start = currentPercent;
      const end = currentPercent + (item.count / total * 100);
      currentPercent = end;
      
      const colors = isRsDashboard ? RS_CHART_GROUP : ['#1162d4', '#60a5fa', '#93c5fd', '#e2e8f0'];
      const color = colors[index % colors.length];
      
      return {
        ...item,
        startPercent: start,
        endPercent: end,
        color,
      };
    });
    
    return { segments, total };
  };
  
  const domainChartData = getDomainChartData();
  
  // Format time display
  const formatTime = (hours) => {
    if (hours >= 1) {
      const h = Math.floor(hours);
      const m = Math.round((hours % 1) * 60);
      return `${h}h ${m}m`;
    }
    return `${Math.round(hours * 60)}m`;
  };
  
  // Format hours display
  const formatHours = (hours) => {
    return t('hrsShort', { count: Math.round(hours) });
  };

  // Format hours as "X hrs Y min" for Standard Time card
  const formatHoursAndMinutes = (hours) => {
    if (hours == null || isNaN(hours)) return t('hrsMin', { h: 0, m: 0 });
    const totalMinutes = Math.round(Number(hours) * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return t('hrsMin', { h, m });
  };

  // Format decimal hours as HH:MM for chart tooltips and Y-axis (e.g. 2.5 -> "2:30")
  const decimalHoursToHHMM = (hours) => {
    if (hours == null || isNaN(hours)) return '0:00';
    const totalMinutes = Math.round(Number(hours) * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}:${String(m).padStart(2, '0')}`;
  };
  
  // Format X-axis labels based on period filter
  const formatChartLabel = (dateStr, dayStr, period) => {
    // #region agent log
    // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:formatChartLabel',message:'formatChartLabel called',data:{dateStr,dayStr,period,hasDateStr:!!dateStr,hasDayStr:!!dayStr},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    
    // For hourly data (Today/Yesterday), use hour labels directly when provided
    if ((period === 'Today' || period === 'Yesterday') && dayStr && dayStr.includes(':')) {
      return dayStr;
    }
    
    // For weekly/monthly buckets, use dayStr directly as it contains formatted ranges
    if (dayStr && (dayStr.includes(' - ') || dayStr.match(/^[A-Z][a-z]{2} \d{4}$/))) {
      return dayStr;
    }
    
    // Fallback to day string if date is missing
    if (!dateStr) {
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:formatChartLabel',message:'dateStr missing, using dayStr fallback',data:{dayStr,period},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
      // #endregion
      return dayStr || '';
    }
    
    try {
      const date = new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:formatChartLabel',message:'date parsed',data:{dateStr,parsedDate:date.toISOString(),period,isValid:!isNaN(date.getTime())},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D'})}).catch(()=>{});
      // #endregion
      
      switch (period) {
        case 'Today':
        case 'Yesterday':
          return dayStr || date.toLocaleDateString(getDateLocale(i18n.language), { weekday: 'short' });
        
        case 'Last 7 Days':
          // Show abbreviated day names (current behavior)
          return dayStr || date.toLocaleDateString(getDateLocale(i18n.language), { weekday: 'short' });
        
        case 'Last 30 Days':
          // Show dates in compact format (e.g., "Jan 1", "Jan 2")
          return date.toLocaleDateString(getDateLocale(i18n.language), { month: 'short', day: 'numeric' });
        
        case 'This Quarter':
          // Backend may provide week ranges in dayStr, use it if available
          if (dayStr && dayStr.includes(' - ')) {
            return dayStr;
          }
          // Otherwise calculate week of quarter
          const quarterStartMonth = Math.floor(date.getMonth() / 3) * 3;
          const quarterStart = new Date(date.getFullYear(), quarterStartMonth, 1);
          const daysDiff = Math.floor((date - quarterStart) / (1000 * 60 * 60 * 24));
          const weekOfQuarter = Math.floor(daysDiff / 7) + 1;
          return `W${weekOfQuarter}`;
        
        case 'YTD':
          // Backend may provide month labels in dayStr (e.g., "Jan 2024")
          if (dayStr && dayStr.match(/^[A-Z][a-z]{2} \d{4}$/)) {
            return dayStr;
          }
          // Show month names (e.g., "Jan", "Feb", "Mar")
          return date.toLocaleDateString(getDateLocale(i18n.language), { month: 'short' });
        
        case 'Custom Range':
          // Backend provides appropriate labels based on range length
          // Use dayStr if it contains formatted ranges, otherwise use date
          if (dayStr && (dayStr.includes(' - ') || dayStr.match(/^[A-Z][a-z]{2} \d{4}$/))) {
            return dayStr;
          }
          // Show dates in compact format
          return date.toLocaleDateString(getDateLocale(i18n.language), { month: 'short', day: 'numeric' });
        
        default:
          // Default: use compact date format
          return date.toLocaleDateString(getDateLocale(i18n.language), { month: 'short', day: 'numeric' });
      }
    } catch (error) {
      // If date parsing fails, fallback to day string
      console.error('Error formatting chart label:', error);
      return dayStr || dateStr || '';
    }
  };
  
  
  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-red-500">{error}</p>
          <button
            onClick={fetchDashboardData}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors"
          >
            {tCommon('retry')}
          </button>
        </div>
      </div>
    );
  }
  
  if (!dashboardData) {
    // #region agent log
    // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:173',message:'dashboardData is null/undefined',data:{loading,error},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    if (loading) {
      return (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <span className="material-symbols-outlined animate-spin text-4xl text-primary">progress_activity</span>
          <p className="text-[#6b7280]">{t('loadingLong')}</p>
        </div>
      );
    }
    return null;
  }
  
  const { kpis, domain_distribution, production_type_distribution, top_operations, top_employees, operations_volume_by_type, filter_options } = dashboardData;
  // #region agent log
  // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:177',message:'destructured dashboardData',data:{hasKpis:!!kpis,kpisValue:kpis,totalOps:kpis?.total_operations,hasFilterOptions:!!filter_options},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
  // #endregion

  const activeDistribution = distributionChartMode === 'domain' ? domain_distribution : production_type_distribution;
  const distributionLabelKey = distributionChartMode === 'domain' ? 'domain' : 'production_type';
  const domainOperationsChartData = getDistributionChartData(activeDistribution || [], distributionLabelKey);
  const activeDistributionList = activeDistribution || [];

  // Filtered LP options for input-with-options filter
  const productionLinesList = filter_options?.production_lines || [];
  const filteredLpOptions = productionLinesList.filter(pl => {
    if (!lpSearchQuery) return true;
    return (pl || '').toLowerCase().includes(lpSearchQuery.toLowerCase());
  });

  // Filtered Program and Project Type options for input-with-options filters
  const programsList = filter_options?.programs || [];
  const filteredProgramOptions = programsList.filter(prog => {
    if (!programSearchQuery) return true;
    return (prog || '').toLowerCase().includes(programSearchQuery.toLowerCase());
  });
  const projectTypesList = filter_options?.project_types || [];
  const filteredProjectTypeOptions = projectTypesList.filter(pt => {
    if (!projectTypeSearchQuery) return true;
    return (pt || '').toLowerCase().includes(projectTypeSearchQuery.toLowerCase());
  });

  // Filtered Domain options for input-with-options multiselect
  const domainsList = filter_options?.domains || [];
  const filteredDomainOptions = domainsList.filter(d => {
    if (!domainSearchQuery) return true;
    return (d || '').toLowerCase().includes(domainSearchQuery.toLowerCase());
  });

  // Prepare chart data for Operations Volume (total or divided by domain)
  const getOperationsVolumeChartData = () => {
    // #region agent log
    // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:getOperationsVolumeChartData',message:'getOperationsVolumeChartData called',data:{hasData:!!operations_volume_by_type,dataLength:operations_volume_by_type?.length,period:filters.period,metric:operationsMetric,firstItem:operations_volume_by_type?.[0]},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
    // #endregion
    if (!operations_volume_by_type || operations_volume_by_type.length === 0) {
      // #region agent log
      // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:getOperationsVolumeChartData',message:'no operations_volume_by_type data',data:{period:filters.period},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion
      return null;
    }
    
    // Format labels based on period filter
    const labels = operations_volume_by_type.map((day, index) => {
      const label = formatChartLabel(day.date, day.day, filters.period);
      // #region agent log
      if (index < 3) { // Log first 3 items
        // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:getOperationsVolumeChartData',message:'label generated',data:{index,day,label,period:filters.period,metric:operationsMetric},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'C'})}).catch(()=>{});
      }
      // #endregion
      return label;
    });

    // When viewing standard hours, build a single-series dataset from standard_hours field
    if (operationsMetric === 'standard') {
      const standardHoursData = operations_volume_by_type.map(day => day.standard_hours || 0);
      const stdLine = isRsDashboard ? RS_CHART_BASE : '#1162d4';

      return {
        labels,
        datasets: [{
          label: t('chartLabels.standardHours'),
          data: standardHoursData,
          backgroundColor: (context) => {
            const chart = context.chart;
            const { ctx, chartArea } = chart;
            if (!chartArea) {
              return isRsDashboard ? 'rgba(42, 42, 42, 0.12)' : '#10b98120';
            }
            if (isRsDashboard) {
              return rsLineAreaGradient(ctx, chartArea);
            }
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, 'rgba(17, 98, 212, 0.4)');
            gradient.addColorStop(0.5, 'rgba(17, 98, 212, 0.1)');
            gradient.addColorStop(1, 'rgba(17, 98, 212, 0.03)');
            return gradient;
          },
          borderColor: stdLine,
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointRadius: 3,
          pointHoverRadius: 5,
          pointBackgroundColor: stdLine,
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
        }],
        domainList: [], // No domains in standard hours mode
      };
    }
    
    // If view by domain is enabled, show bar chart with imputations divided by domain only
    if (executedOpsViewByDomain) {
      // Keys to exclude when extracting domain names (non-domain metadata)
      const nonDomainKeys = new Set([
        'day', 'date', 'completed', 'incomplete',
        'standard_hours', 'total_production_client_hours', 'total_contrainte_hours',
        'count_contrainte_imputations', 'count_production_client_imputations', 'count_distinct_production_type',
        'EN_ATTENTE', 'TERMINE', 'ECHEC', 'ANNULE',
        ...RS_ETAT_CODES,
      ]);
      
      // Extract all unique domains from the data (exclude non-domain keys)
      const domainKeys = new Set();
      operations_volume_by_type.forEach(day => {
        Object.keys(day).forEach(key => {
          if (!nonDomainKeys.has(key)) {
            domainKeys.add(key);
          }
        });
      });
      
      // Sort domains for consistent ordering
      const sortedDomains = Array.from(domainKeys).sort();
      
      const colorPalette = isRsDashboard
        ? RS_CHART_SERIES
        : ['#1162d4', '#60a5fa', '#93c5fd', '#cbd5e1', '#fbbf24', '#f59e0b', '#ef4444', '#10b981', '#8b5cf6', '#ec4899'];
      
      // Generate datasets dynamically based on domains
      const datasets = sortedDomains.map((domain, index) => {
        const color = colorPalette[index % colorPalette.length];
        const domainData = operations_volume_by_type.map(day => day[domain] || 0);
        
        return {
          label: domain,
          data: domainData,
          backgroundColor: color,
          borderColor: color,
          borderWidth: 0,
        };
      });
      
      return {
        labels,
        datasets,
        domainList: sortedDomains, // Store domain list for legend
      };
    }
    
    // Default view: Line chart showing total operations
    // Check if we have separate completed/incomplete data (when toggle is enabled)
    const hasSeparateData = operations_volume_by_type.length > 0 && 
                            ('completed' in operations_volume_by_type[0] || 'incomplete' in operations_volume_by_type[0]);
    
    // Check if we have all statuses separately (when showAllStatuses toggle is enabled)
    const firstVol = operations_volume_by_type[0] || {};
    const hasRsStatuses = RS_ETAT_CODES.some((k) => k in firstVol);
    const hasAllStatuses = operations_volume_by_type.length > 0 &&
                          ('EN_ATTENTE' in firstVol ||
                           'TERMINE' in firstVol || 'ECHEC' in firstVol ||
                           'ANNULE' in firstVol || hasRsStatuses);
    
    if (hasAllStatuses && !executedOpsViewByDomain) {
      const isRs = (getTenantSubdomain() || '').toLowerCase() === 'rs';
      const executedOpsStatuses = isRs
        ? RS_ETAT_CODES
        : ['TERMINE', 'ECHEC'];
      const statusColors = isRs
        ? RS_CHART_STATUS_LINE
        : {
            TERMINE: { border: '#10b981', bg: 'rgba(16, 185, 129, 0.4)' },
            ECHEC: { border: '#ef4444', bg: 'rgba(239, 68, 68, 0.4)' },
          };
      const statusLabels = isRs
        ? {
            TO_DO: t('statuses.toDo'),
            QUALIFIED: t('statuses.qualified'),
            IN_PROGRESS: t('statuses.inProgress'),
            SOURCED: t('statuses.sourced'),
            TO_PROPOS: t('statuses.toPropos'),
            PROPOSED: t('statuses.proposed'),
            PRESENTED: t('statuses.presented'),
            FINAL: t('statuses.final'),
            COMPLETED: t('statuses.completed'),
          }
        : {
            TERMINE: t('statuses.termine'),
            ECHEC: t('statuses.echec'),
          };
      const datasets = executedOpsStatuses.map(status => ({
        label: statusLabels[status],
        data: operations_volume_by_type.map(day => day[status] || 0),
        backgroundColor: (context) => {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) {
            return statusColors[status].bg;
          }
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          const bgColor = statusColors[status].bg;
          gradient.addColorStop(0, bgColor);
          gradient.addColorStop(0.5, bgColor.replace('0.4', '0.1'));
          gradient.addColorStop(1, bgColor.replace('0.4', '0.03'));
          return gradient;
        },
        borderColor: statusColors[status].border,
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: statusColors[status].border,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }));
      
      return {
        labels,
        datasets,
        domainList: [],
      };
    } else if (hasSeparateData) {
      // Show two separate lines: completed and incomplete
      const completedData = operations_volume_by_type.map(day => day.completed || 0);
      const incompleteData = operations_volume_by_type.map(day => day.incomplete || 0);
      
      const accLine = isRsDashboard ? RS_CHART_BASE : '#1162d4';
      return {
        labels,
        datasets: [
          {
            label: t('chartLabels.accomplishedOperations'),
            data: completedData,
            backgroundColor: (context) => {
              const chart = context.chart;
              const {ctx, chartArea} = chart;
              if (!chartArea) {
                return isRsDashboard ? 'rgba(42, 42, 42, 0.12)' : '#1162d420';
              }
              if (isRsDashboard) {
                return rsLineAreaGradient(ctx, chartArea);
              }
              const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0, 'rgba(17, 98, 212, 0.4)');
              gradient.addColorStop(0.5, 'rgba(17, 98, 212, 0.1)');
              gradient.addColorStop(1, 'rgba(17, 98, 212, 0.03)');
              return gradient;
            },
            borderColor: accLine,
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: accLine,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
          },
          {
            label: t('chartLabels.incompleteOperations'),
            data: incompleteData,
            backgroundColor: (context) => {
              const chart = context.chart;
              const {ctx, chartArea} = chart;
              if (!chartArea) {
                return '#6b728020';
              }
              const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
              gradient.addColorStop(0, 'rgba(107, 114, 128, 0.4)');
              gradient.addColorStop(0.5, 'rgba(107, 114, 128, 0.1)');
              gradient.addColorStop(1, 'rgba(107, 114, 128, 0.03)');
              return gradient;
            },
            borderColor: '#6b7280',
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: '#6b7280',
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
          }
        ],
        domainList: [], // No domains for total view
      };
    }
    
    // Calculate total operations for each time period (sum across all domains)
    const totalData = operations_volume_by_type.map(day => {
      let total = 0;
      // Sum all domain values (exclude non-count keys)
      Object.keys(day).forEach(key => {
        if (key !== 'day' && key !== 'date' && key !== 'standard_hours') {
          total += day[key] || 0;
        }
      });
      return total;
    });
    
    const totalLine = isRsDashboard ? RS_CHART_BASE : '#1162d4';
    return {
      labels,
      datasets: [{
        label: t('chartLabels.totalOperations'),
        data: totalData,
        backgroundColor: (context) => {
          const chart = context.chart;
          const {ctx, chartArea} = chart;
          if (!chartArea) {
            return isRsDashboard ? 'rgba(42, 42, 42, 0.12)' : '#1162d420';
          }
          if (isRsDashboard) {
            return rsLineAreaGradient(ctx, chartArea);
          }
          const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
          gradient.addColorStop(0, 'rgba(17, 98, 212, 0.4)'); // Top: 40% opacity
          gradient.addColorStop(0.5, 'rgba(17, 98, 212, 0.1)'); // Middle: 25% opacity
          gradient.addColorStop(1, 'rgba(17, 98, 212, 0.03)'); // Bottom: 10% opacity
          return gradient;
        },
        borderColor: totalLine,
        borderWidth: 2,
        fill: true,
        tension: 0.4, // Smooth curves
        pointRadius: 3,
        pointHoverRadius: 5,
        pointBackgroundColor: totalLine,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      }],
      domainList: [], // No domains for total view
    };
  };
  
  const operationsVolumeChartResult = getOperationsVolumeChartData();
  const operationsVolumeChartData = operationsVolumeChartResult?.labels ? {
    labels: operationsVolumeChartResult.labels,
    datasets: operationsVolumeChartResult.datasets
  } : null;
  const operationsVolumeDomains = operationsVolumeChartResult?.domainList || [];
  
  // Prepare chart data for Top Performing Employees
  const getTopEmployeesChartData = () => {
    if (!top_employees || top_employees.length === 0) {
      return null;
    }
    
    // Limit to max 5 items
    const limitedEmployees = top_employees.slice(0, 5);
    
    // Extract employee names for x-axis labels (vertical chart)
    const labels = limitedEmployees.map(emp => {
      // Truncate long names to prevent chart overflow
      const name = emp.name || t('unknown');
      return name.length > 20 ? name.substring(0, 17) + '...' : name;
    });
    
    // Extract operations count values
    const data = limitedEmployees.map(emp => emp.count || 0);
    
    // Store full employee data for tooltips
    const employeeData = limitedEmployees.map(emp => ({
      name: emp.name || t('unknown'),
      email: emp.email || '-',
      count: emp.count || 0
    }));
    
    const bar = isRsDashboard ? RS_CHART_BASE : '#1162d4';
    return {
      labels,
      datasets: [{
        label: t('chartLabels.operationsAccomplished'),
        data: data,
        backgroundColor: bar,
        borderColor: bar,
        borderWidth: 0,
      }],
      employeeData, // Store for tooltip callbacks
    };
  };
  
  const topEmployeesChartResult = getTopEmployeesChartData();
  const topEmployeesChartData = topEmployeesChartResult ? {
    labels: topEmployeesChartResult.labels,
    datasets: topEmployeesChartResult.datasets
  } : null;
  
  // Prepare chart data for Top Operations (dual metric: count and employee count)
  const getTopOperationsChartData = () => {
    if (!top_operations || top_operations.length === 0) {
      return null;
    }
    
    // Limit to max 5 items
    const limitedOperations = top_operations.slice(0, 5);
    
    // Extract operation names for x-axis labels (vertical chart)
    const labels = limitedOperations.map(op => {
      // Truncate long names to prevent chart overflow
      const name = op.activity || t('unknown');
      return name.length > 25 ? name.substring(0, 22) + '...' : name;
    });
    
    // Extract operations count values
    const countData = limitedOperations.map(op => op.count || 0);
    
    // Extract employee count values (number of distinct employees who performed each operation)
    const employeeCountData = limitedOperations.map(op => op.employee_count || 0);
    
    // Store full operation data for tooltips
    const operationData = limitedOperations.map(op => ({
      name: op.activity || t('unknown'),
      count: op.count || 0,
      employee_count: op.employee_count || 0
    }));
    
    const dual = isRsDashboard ? RS_CHART_DUAL_BAR : { primary: '#1162d4', secondary: '#60a5fa' };
    return {
      labels,
      datasets: [
        {
          label: t('chartLabels.operationsCount'),
          data: countData,
          backgroundColor: dual.primary,
          borderColor: dual.primary,
          borderWidth: 0,
          yAxisID: 'y',
        },
        {
          label: t('chartLabels.employeeCount'),
          data: employeeCountData,
          backgroundColor: dual.secondary,
          borderColor: dual.secondary,
          borderWidth: 0,
          yAxisID: 'y1',
        }
      ],
      operationData, // Store for tooltip callbacks
    };
  };
  
  const topOperationsChartResult = getTopOperationsChartData();
  const topOperationsChartData = topOperationsChartResult ? {
    labels: topOperationsChartResult.labels,
    datasets: topOperationsChartResult.datasets
  } : null;
  
  // Chart options for Executed Operations Overview (line chart)
  const operationsVolumeLineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      legend: {
        display: operationsVolumeChartData?.datasets?.length > 1, // Show legend when multiple datasets (completed/incomplete)
        position: 'top',
        labels: {
          color: chartTextColor,
          font: {
            size: 11,
          },
          padding: 12,
          usePointStyle: true,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: chartTooltipBg,
        padding: 12,
        titleFont: {
          size: 12,
          weight: 'bold',
        },
        bodyFont: {
          size: 11,
        },
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y || 0;
            if (operationsMetric === 'standard') {
              return `${label}: ${decimalHoursToHHMM(value)}`;
            }
            return `${label}: ${Number(value).toLocaleString()}`;
          },
          footer: function(tooltipItems) {
            const total = tooltipItems.reduce((sum, item) => sum + (item.parsed.y || 0), 0);
            if (operationsMetric === 'standard') {
              return t('chartLabels.totalValue', { value: decimalHoursToHHMM(total) });
            }
            return t('chartLabels.totalValue', { value: Number(total).toLocaleString() });
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: chartTextColor,
          font: {
            size: 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: chartGridColor,
        },
        ticks: {
          color: chartTextColor,
          font: {
            size: 11,
          },
          callback: function(value) {
            if (operationsMetric === 'standard') {
              return decimalHoursToHHMM(value);
            }
            return Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 });
          },
        },
      },
    },
  };
  
  // Chart options for Executed Operations Overview (bar chart by domain)
  const operationsVolumeBarChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: operationsVolumeChartData?.datasets?.length === 2 && operationsVolumeDomains.length === 0, // Show legend when showing completed/incomplete (2 datasets, no domains)
        position: 'top',
        labels: {
          color: chartTextColor,
          font: {
            size: 11,
          },
          padding: 12,
          usePointStyle: true,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: chartTooltipBg,
        padding: 12,
        titleFont: {
          size: 12,
          weight: 'bold',
        },
        bodyFont: {
          size: 11,
        },
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y || 0;
            if (operationsMetric === 'standard') {
              return `${label}: ${decimalHoursToHHMM(value)}`;
            }
            return `${label}: ${Number(value).toLocaleString()}`;
          },
          footer: function(tooltipItems) {
            const total = tooltipItems.reduce((sum, item) => sum + (item.parsed.y || 0), 0);
            if (operationsMetric === 'standard') {
              return t('chartLabels.totalValue', { value: decimalHoursToHHMM(total) });
            }
            return t('chartLabels.totalValue', { value: Number(total).toLocaleString() });
          },
        },
      },
    },
    scales: {
      x: {
        stacked: operationsVolumeDomains.length > 0, // Stack when showing domains, group when showing completed/incomplete
        grid: {
          display: false,
        },
        ticks: {
          color: chartTextColor,
          font: {
            size: 11,
          },
        },
      },
      y: {
        stacked: operationsVolumeDomains.length > 0, // Stack when showing domains, group when showing completed/incomplete
        beginAtZero: true,
        grid: {
          color: chartGridColor,
        },
        ticks: {
          color: chartTextColor,
          font: {
            size: 11,
          },
          callback: function(value) {
            if (operationsMetric === 'standard') {
              return decimalHoursToHHMM(value);
            }
            return Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 });
          },
        },
      },
    },
  };
  
  // Use appropriate chart options based on view mode
  const operationsVolumeChartOptions = executedOpsViewByDomain 
    ? operationsVolumeBarChartOptions 
    : operationsVolumeLineChartOptions;
  
  // Chart options for nested donut chart - operations (outer ring)
  const domainOperationsOuterChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '50%', // Outer ring cutout
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: chartTooltipBg,
        padding: 14,
        titleFont: {
          size: 13,
          weight: 'bold',
        },
        bodyFont: {
          size: 11,
        },
        displayColors: true,
        callbacks: {
          title: function(context) {
            if (!context || !context[0]) return t('chartLabels.operation');
            const dataIndex = context[0].dataIndex;
            const metadata = domainOperationsChartData?.outerMetadata?.[dataIndex];
            if (metadata && metadata.operationName) {
              return metadata.operationName;
            }
            return context[0].label || t('chartLabels.operation');
          },
          label: function(context) {
            if (!context) return [];
            const dataIndex = context.dataIndex;
            const metadata = domainOperationsChartData?.outerMetadata?.[dataIndex];
            const value = context.dataset?.data[dataIndex] || 0;
            const total = context.dataset?.data?.reduce((sum, val) => sum + (val || 0), 0) || 1;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            
            const lines = [];
            if (metadata?.domain) {
              lines.push(t('chartLabels.domainValue', { domain: metadata.domain }));
            }
            lines.push(t('chartLabels.operationsValue', { value: value.toLocaleString() }));
            lines.push(t('chartLabels.percentage', { value: percentage }));
            
            return lines;
          },
          footer: function(tooltipItems) {
            if (!tooltipItems || tooltipItems.length === 0) return '';
            const total = tooltipItems.reduce((sum, item) => {
              return sum + (item.dataset?.data[item.dataIndex] || 0);
            }, 0);
            return t('chartLabels.totalOperationsFooter', { count: total.toLocaleString() });
          },
        },
      },
    },
  };
  
  // Chart options for nested donut chart - domains (inner ring)
  const domainOperationsInnerChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '70%', // Inner ring cutout
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: chartTooltipBg,
        padding: 14,
        titleFont: {
          size: 13,
          weight: 'bold',
        },
        bodyFont: {
          size: 11,
        },
        displayColors: true,
        callbacks: {
          title: function(context) {
            if (!context || !context[0]) return t('chartLabels.domain');
            const dataIndex = context[0].dataIndex;
            const metadata = domainOperationsChartData?.innerMetadata?.[dataIndex];
            if (metadata && metadata.domain) {
              return metadata.domain;
            }
            return context[0].label || t('chartLabels.domain');
          },
          label: function(context) {
            if (!context) return [];
            const dataIndex = context.dataIndex;
            const metadata = domainOperationsChartData?.innerMetadata?.[dataIndex];
            const value = context.dataset?.data[dataIndex] || 0;
            const total = context.dataset?.data?.reduce((sum, val) => sum + (val || 0), 0) || 1;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            
            const lines = [];
            lines.push(t('chartLabels.operationsValue', { value: value.toLocaleString() }));
            
            // Show status breakdown if showAllStatuses is enabled (Accomplished and Failed)
            if (metadata && metadata.status_breakdown && filters.showAllStatuses) {
              const statusBreakdown = metadata.status_breakdown;
              const statusLines = [];
              Object.keys(statusBreakdownLegendMap).forEach((status) => {
                const count = statusBreakdown[status] || 0;
                if (count > 0) {
                  const short =
                    status === 'TERMINE'
                      ? t('chartLabels.completed')
                      : status === 'ECHEC'
                        ? t('chartLabels.failed')
                        : statusBreakdownLegendMap[status]?.label || status;
                  statusLines.push(`${short}: ${count.toLocaleString()}`);
                }
              });
              
              if (statusLines.length > 0) {
                lines.push(''); // Empty line separator
                lines.push(...statusLines);
              }
            } else if (metadata && (metadata.completed_count !== undefined || metadata.incomplete_count !== undefined)) {
              // Show completed and incomplete breakdown if available (when toggle is enabled but not showAllStatuses)
              const completedCount = metadata.completed_count || 0;
              const incompleteCount = metadata.incomplete_count || 0;
              
              if (completedCount > 0 || incompleteCount > 0) {
                lines.push(t('chartLabels.accomplishedValue', { count: completedCount.toLocaleString() }));
                if (incompleteCount > 0) {
                  lines.push(t('chartLabels.incompleteValue', { count: incompleteCount.toLocaleString() }));
                }
              }
            }
            
            if (metadata?.percentage !== undefined) {
              lines.push(t('chartLabels.percentage', { value: metadata.percentage.toFixed(1) }));
            } else {
              lines.push(t('chartLabels.percentage', { value: percentage }));
            }
            
            return lines;
          },
          footer: function(tooltipItems) {
            if (!tooltipItems || tooltipItems.length === 0) return '';
            const total = tooltipItems.reduce((sum, item) => {
              return sum + (item.dataset?.data[item.dataIndex] || 0);
            }, 0);
            return t('chartLabels.totalOperationsFooter', { count: total.toLocaleString() });
          },
        },
      },
    },
  };
  
  // Legacy chart options (kept for backward compatibility)
  const domainOperationsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '60%', // Inner ring cutout
    plugins: {
      legend: {
        display: false, // Legend is shown separately
      },
      tooltip: {
        backgroundColor: chartTooltipBg,
        padding: 12,
        titleFont: {
          size: 12,
          weight: 'bold',
        },
        bodyFont: {
          size: 11,
        },
        callbacks: {
          label: function(context) {
            const label = context.label || '';
            const value = context.dataset.data[context.dataIndex] || 0;
            const total = context.dataset.data.reduce((sum, val) => sum + (val || 0), 0);
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${label}: ${value.toLocaleString()} (${percentage}%)`;
          },
        },
      },
    },
  };
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: chartTooltipBg,
        padding: 12,
        titleFont: {
          size: 12,
          weight: 'bold',
        },
        bodyFont: {
          size: 11,
        },
        callbacks: {
          label: function(context) {
            const label = context.dataset.label || '';
            const value = context.parsed.y || 0;
            return `${label}: ${value}`;
          },
          footer: function(tooltipItems) {
            const total = tooltipItems.reduce((sum, item) => sum + (item.parsed.y || 0), 0);
            return t('chartLabels.totalValue', { value: total });
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        },
        ticks: {
          color: chartTextColor,
          font: {
            size: 11,
          },
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        grid: {
          color: chartGridColor,
        },
        ticks: {
          color: chartTextColor,
          font: {
            size: 11,
          },
          precision: 0,
        },
      },
    },
  };
  
  // Chart options for Top Performing Employees (vertical bar chart)
  const topEmployeesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: chartTooltipBg,
        padding: 12,
        titleFont: {
          size: 12,
          weight: 'bold',
        },
        bodyFont: {
          size: 11,
        },
        callbacks: {
          title: function(context) {
            if (topEmployeesChartResult && topEmployeesChartResult.employeeData) {
              const index = context[0].dataIndex;
              const employee = topEmployeesChartResult.employeeData[index];
              return employee ? employee.name : '';
            }
            return '';
          },
          label: function(context) {
            if (topEmployeesChartResult && topEmployeesChartResult.employeeData) {
              const index = context.dataIndex;
              const employee = topEmployeesChartResult.employeeData[index];
              if (employee) {
                return [
                  t('chartLabels.emailValue', { email: employee.email }),
                  t('chartLabels.operationsValue', { value: employee.count.toLocaleString() })
                ];
              }
            }
            return t('chartLabels.operationsValue', { value: context.parsed.y || 0 });
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: chartTextColor,
          font: {
            size: 11,
          },
          stepSize: 1,
          maxRotation: 0,
          minRotation: 0,
          callback: function(value, index, ticks) {
            // Only show label for each category (one per column)
            if (value % 1 === 0) {
              return index + 1;
            }
            return '';
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: chartGridColor,
        },
        ticks: {
          color: chartTextColor,
          font: {
            size: 11,
          },
          precision: 0,
          callback: function(value) {
            return value.toLocaleString();
          },
        },
      },
    },
  };
  
  // Chart options for Top Operations (vertical grouped bar chart with dual metrics)
  const topOperationsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: chartTextColor,
          font: {
            size: 11,
          },
          padding: 12,
          usePointStyle: true,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        backgroundColor: chartTooltipBg,
        padding: 12,
        titleFont: {
          size: 12,
          weight: 'bold',
        },
        bodyFont: {
          size: 11,
        },
        callbacks: {
          title: function(context) {
            if (topOperationsChartResult && topOperationsChartResult.operationData) {
              const index = context[0].dataIndex;
              const operation = topOperationsChartResult.operationData[index];
              return operation ? operation.activity : '';
            }
            return '';
          },
          label: function(context) {
            if (topOperationsChartResult && topOperationsChartResult.operationData) {
              const index = context.dataIndex;
              const operation = topOperationsChartResult.operationData[index];
              if (operation) {
                const datasetLabel = context.dataset.label || '';
                const value = context.parsed.y || 0;
                
                if (datasetLabel === t('chartLabels.operationsCount')) {
                  return t('chartLabels.countValue', { value: value.toLocaleString() });
                } else if (datasetLabel === t('chartLabels.employeeCount')) {
                  return t('chartLabels.employeesValue', { value: value.toLocaleString() });
                }
              }
            }
            const datasetLabel = context.dataset.label || '';
            const value = context.parsed.y || 0;
            return `${datasetLabel}: ${value.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      x: {
        display: true,
        grid: {
          display: false,
        },
        ticks: {
          color: chartTextColor,
          font: {
            size: 11,
          },
          stepSize: 1,
          maxRotation: 0,
          minRotation: 0,
          callback: function(value, index, ticks) {
            // Only show label for each category (one per column/bar group)
            if (value % 1 === 0) {
              return index + 1;
            }
            return '';
          },
        },
      },
      y: {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        grid: {
          display: false, // Hide grid for right axis to avoid overlap
        },
        ticks: {
          color: topOpsAxisPrimary,
          font: {
            size: 11,
            weight: 'bold',
          },
          precision: 0,
          callback: function(value) {
            return value.toLocaleString();
          },
        },
        title: {
          display: true,
          text: t('chartLabels.operationsCount'),
          color: topOpsAxisPrimary,
          font: {
            size: 11,
            weight: 'bold',
          },
        },
      },
      y1: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        grid: {
          color: chartGridColor,
        },
        ticks: {
          color: topOpsAxisSecondary,
          font: {
            size: 11,
            weight: 'bold',
          },
          precision: 0,
          callback: function(value) {
            return value.toLocaleString();
          },
        },
        title: {
          display: true,
          text: t('chartLabels.employeeCount'),
          color: topOpsAxisSecondary,
          font: {
            size: 11,
            weight: 'bold',
          },
        },
      },
    },
  };
  
  return (
    <div className="flex flex-col gap-4">
      {/* Toggle Switch Styles */}
      <style>{`.toggle-switch {
  font-size: 10px;
  position: relative;
  display: inline-block;
  width: 3.5em;
  height: 2em;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgb(182, 182, 182);
  transition: .4s;
  border-radius: 10px;
}
.toggle-slider:before {
  position: absolute;
  content: "";
  height: 1.4em;
  width: 1.4em;
  border-radius: 8px;
  left: 0.3em;
  bottom: 0.3em;
  background-color: rgb(255, 255, 255);
  transition: .4s;
}
.toggle-switch input:checked + .toggle-slider {
  background-color: ${isRsDashboard ? '#2a2a2a' : '#124191'};
}
.toggle-switch input:focus + .toggle-slider {
  box-shadow: 0 0 1px #2196F3;
}
.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(1.5em);
}
      `}</style>
      
      {/* Page Header & Key Actions */}
      
      {/* Global Filters Ribbon */}
      <div className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-[#e5e7eb] dark:border-slate-600 shadow-sm transition-colors duration-200">
        <div className="flex flex-row gap-2 mb-2 justify-between items-center">
            <div className="flex items-center gap-2 text-[#111418] dark:text-slate-100 font-semibold text-sm">
              <span className="material-symbols-outlined text-[20px] text-primary">filter_alt</span>
              {t('filters')}
            </div>
            
            <div className="flex items-center gap-3 p-3 rounded-lg flex-wrap">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium text-gray cursor-pointer" onClick={() => handleFilterChange('includeIncompleteOperations', !filters.includeIncompleteOperations)}>
                  {t('includeIncompleteOperations')} 
                </span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={filters.includeIncompleteOperations}
                    onChange={(e) => handleFilterChange('includeIncompleteOperations', e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              {filters.includeIncompleteOperations && (
                <div className="flex items-center gap-3">
                  <span className="text-sm font-medium text-gray cursor-pointer" onClick={() => handleFilterChange('showAllStatuses', !filters.showAllStatuses)}>
                    {t('showAllStatuses')} 
                  </span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={filters.showAllStatuses}
                      onChange={(e) => handleFilterChange('showAllStatuses', e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              )}
            </div>
          </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
           <div >
             <label className="flex flex-col gap-1.5">
               <span className="text-xs font-medium text-[#617289]">{t('period')}</span>
               <select
                 className="form-select block w-full rounded-lg border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 text-sm text-[#111418] dark:text-slate-100 focus:border-primary focus:ring-primary h-10"
                 value={filters.period}
                 onChange={(e) => handleFilterChange('period', e.target.value)}
               >
                 <option value="Today">{t('periods.today')}</option>
                 <option value="Yesterday">{t('periods.yesterday')}</option>
                 <option value="Last 7 Days">{t('periods.last7Days')}</option>
                 <option value="Last 30 Days">{t('periods.last30Days')}</option>
                 <option value="This Quarter">{t('periods.thisQuarter')}</option>
                 {/* <option>YTD</option> */}
                 <option value="Custom Range">{t('periods.customRange')}</option>
               </select>
             </label>
           </div>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#617289]">{t('center')}</span>
            <select
              className="form-select block w-full rounded-lg border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 text-sm text-[#111418] dark:text-slate-100 focus:border-primary focus:ring-primary h-10"
              value={filters.centre || ''}
              onChange={(e) => handleFilterChange('centre', e.target.value)}
            >
              <option value="">{t('allCenters')}</option>
              {filter_options?.centres?.map((centre) => (
                <option key={centre.id} value={String(centre.id)}>{centre.name}</option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#617289]">{t('team')}</span>
            <select
              className="form-select block w-full rounded-lg border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 text-sm text-[#111418] dark:text-slate-100 focus:border-primary focus:ring-primary h-10"
              value={filters.team || ''}
              onChange={(e) => handleFilterChange('team', e.target.value)}
            >
              <option value="">{t('allTeams')}</option>
              {(filter_options?.teams ?? []).map((team) => (
                <option key={team.id} value={String(team.id)}>{team.name}</option>
              ))}
            </select>
          </label>
           <label className="flex flex-col gap-1.5">
             <span className="text-xs font-medium text-[#617289]">{t('employee')}</span>
             <div className="relative">
               <div className="relative">
                 <input
                   type="text"
                   value={(employeeSearchQuery ?? '').length > 0 ? (employeeSearchQuery ?? '') : (filters.employee ? getSelectedEmployeeName() : '')}
                   onChange={(e) => {
                     setEmployeeSearchQuery(e.target.value);
                     if (!e.target.value) {
                       handleFilterChange('employee', '');
                     }
                     setEmployeeDropdownOpen(true);
                   }}
                   onFocus={() => setEmployeeDropdownOpen(true)}
                   onBlur={() => {
                     setTimeout(() => {
                       setEmployeeDropdownOpen(false);
                       if (filters.employee) setEmployeeSearchQuery('');
                     }, 200);
                   }}
                   className="h-10 rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 px-4 pr-10 text-sm text-[#111418] dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary w-full disabled:opacity-50 disabled:cursor-not-allowed placeholder-slate-500 dark:placeholder-slate-400"
                   placeholder={t('searchByNameEmail')}
                 />
                 <span className="material-symbols-outlined absolute right-3 top-2.5 text-[#617289] text-[20px] pointer-events-none">arrow_drop_down</span>
               </div>
               
               {/* Custom Dropdown */}
               <AnimatePresence>
                 {employeeDropdownOpen && (
                   <motion.div
                     initial={{ opacity: 0, y: -10 }}
                     animate={{ opacity: 1, y: 0 }}
                     exit={{ opacity: 0, y: -10 }}
                     transition={{ duration: 0.2 }}
                     className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg border border-[#dbe0e6] dark:border-slate-600 shadow-lg max-h-60 overflow-auto"
                   >
                     {filteredEmployees.length > 0 ? (
                       <div className="p-2">
                         <p className="text-xs font-semibold text-[#617289] uppercase tracking-wider px-2 py-1 mb-1">
                           {t('employees')}
                         </p>
                         <button
                           type="button"
                           onClick={() => {
                             setEmployeeSearchQuery('');
                             handleFilterChange('employee', '');
                             setEmployeeDropdownOpen(false);
                           }}
                           className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2"
                         >
                           <span className="material-symbols-outlined text-[18px] text-[#617289]">person_off</span>
                           <span>{t('allEmployees')}</span>
                         </button>
                         {filteredEmployees.map((employee) => (
                           <button
                             key={employee.id}
                             type="button"
                             onClick={() => {
                               setEmployeeSearchQuery('');
                               handleFilterChange('employee', employee.id.toString());
                               setEmployeeDropdownOpen(false);
                             }}
                             className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2"
                           >
                             <span className="material-symbols-outlined text-[18px] text-[#617289]">person</span>
                             <div className="flex flex-col">
                               <span className="font-medium">
                                 {employee.name}
                               </span>
                               <span className="text-xs text-[#617289]">{employee.email}</span>
                             </div>
                           </button>
                         ))}
                       </div>
                     ) : (
                       <div className="p-3 text-center text-sm text-[#617289]">
                         {employeeSearchQuery ? t('noMatchingEmployees') : t('noEmployeesAvailable')}
                       </div>
                     )}
                   </motion.div>
                 )}
               </AnimatePresence>
             </div>
           </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#617289]">{t('domain')}</span>
            <div className="relative" ref={domainDropdownRef}>
              <div className="relative">
                <input
                  type="text"
                  value={domainDropdownOpen ? domainSearchQuery : (Array.isArray(filters.domain) && filters.domain.length > 0 ? (filters.domain.length === 1 ? filters.domain[0] : t('domainsSelected', { count: filters.domain.length })) : '')}
                  onChange={(e) => {
                    setDomainSearchQuery(e.target.value);
                    setDomainDropdownOpen(true);
                    setEmployeeDropdownOpen(false);
                    setOperationDropdownOpen(false);
                  }}
                  onFocus={() => {
                    setDomainDropdownOpen(true);
                    setDomainSearchQuery(Array.isArray(filters.domain) && filters.domain.length > 0 ? '' : domainSearchQuery);
                    setEmployeeDropdownOpen(false);
                    setOperationDropdownOpen(false);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setDomainDropdownOpen(false);
                      setDomainSearchQuery('');
                    }, 200);
                  }}
                  className="h-10 rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 px-4 pr-10 text-sm text-[#111418] dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary w-full placeholder-slate-500 dark:placeholder-slate-400"
                  placeholder={t('searchOrSelectDomains')}
                />
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-[#617289] text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
              <AnimatePresence>
                {domainDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg border border-[#dbe0e6] dark:border-slate-600 shadow-lg max-h-60 overflow-auto"
                  >
                    <div className="p-2">
                      <p className="text-xs font-semibold text-[#617289] uppercase tracking-wider px-2 py-1 mb-1">
                        {t('domainsMultiSelect')}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          handleFilterChange('domain', []);
                          setDomainDropdownOpen(false);
                          setDomainSearchQuery('');
                        }}
                        className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <span className="material-symbols-outlined text-[18px] text-[#617289]">close</span>
                        <span>{t('allDomains')}</span>
                      </button>
                      {filteredDomainOptions.length > 0 ? (
                        filteredDomainOptions.map((domain) => {
                          const selected = Array.isArray(filters.domain) && filters.domain.includes(domain);
                          return (
                            <button
                              key={domain}
                              type="button"
                              onClick={() => {
                                const current = Array.isArray(filters.domain) ? [...filters.domain] : [];
                                const next = selected
                                  ? current.filter((d) => d !== domain)
                                  : [...current, domain];
                                handleFilterChange('domain', next);
                              }}
                              className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2"
                            >
                              <span className="material-symbols-outlined text-[18px] text-[#617289]">
                                {selected ? 'check_box' : 'check_box_outline_blank'}
                              </span>
                              <span>{domain}</span>
                            </button>
                          );
                        })
                      ) : (
                        <div className="p-3 text-center text-sm text-[#617289]">
                          {domainSearchQuery ? t('noMatchingDomains') : t('noDomainsAvailable')}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#617289]">{t('productionLine')}</span>
            <div className="relative" ref={lpDropdownRef}>
              <div className="relative">
                <input
                  type="text"
                  value={lpDropdownOpen ? lpSearchQuery : (filters.production_line_lp || '')}
                  onChange={(e) => {
                    setLpSearchQuery(e.target.value);
                    if (!e.target.value) {
                      handleFilterChange('production_line_lp', '');
                    }
                    setLpDropdownOpen(true);
                  }}
                  onFocus={() => setLpDropdownOpen(true)}
                  onBlur={() => {
                    setTimeout(() => {
                      setLpDropdownOpen(false);
                      if (filters.production_line_lp) setLpSearchQuery('');
                    }, 200);
                  }}
                  className="h-10 rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 px-4 pr-10 text-sm text-[#111418] dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary w-full placeholder-slate-500 dark:placeholder-slate-400"
                  placeholder={t('searchOrSelectLine')}
                />
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-[#617289] text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
              <AnimatePresence>
                {lpDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg border border-[#dbe0e6] dark:border-slate-600 shadow-lg max-h-60 overflow-auto"
                  >
                    <div className="p-2">
                      <p className="text-xs font-semibold text-[#617289] uppercase tracking-wider px-2 py-1 mb-1">
                        {t('productionLines')}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          setLpSearchQuery('');
                          handleFilterChange('production_line_lp', '');
                          setLpDropdownOpen(false);
                        }}
                        className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <span className="material-symbols-outlined text-[18px] text-[#617289]">close</span>
                        <span>{t('allLines')}</span>
                      </button>
                      {filteredLpOptions.length > 0 ? (
                        filteredLpOptions.map((pl) => (
                          <button
                            key={pl}
                            type="button"
                            onClick={() => {
                              setLpSearchQuery('');
                              handleFilterChange('production_line_lp', pl);
                              setLpDropdownOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2"
                          >
                            <span className="material-symbols-outlined text-[18px] text-[#617289]">linear_scale</span>
                            <span>{pl}</span>
                          </button>
                        ))
                      ) : (
                        <div className="p-3 text-center text-sm text-[#617289]">
                          {lpSearchQuery ? t('noMatchingLines') : t('noLinesAvailable')}
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#617289]">{t('program')}</span>
            <div className="relative" ref={programDropdownRef}>
              <div className="relative">
                <input
                  type="text"
                  value={programDropdownOpen ? programSearchQuery : (filters.program || '')}
                  onChange={(e) => {
                    setProgramSearchQuery(e.target.value);
                    if (!e.target.value) handleFilterChange('program', '');
                    setProgramDropdownOpen(true);
                  }}
                  onFocus={() => setProgramDropdownOpen(true)}
                  onBlur={() => {
                    setTimeout(() => {
                      setProgramDropdownOpen(false);
                      if (filters.program) setProgramSearchQuery('');
                    }, 200);
                  }}
                  className="h-10 rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 px-4 pr-10 text-sm text-[#111418] dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary w-full placeholder-slate-500 dark:placeholder-slate-400"
                  placeholder={t('searchOrSelectProgram')}
                />
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-[#617289] text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
              <AnimatePresence>
                {programDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg border border-[#dbe0e6] dark:border-slate-600 shadow-lg max-h-60 overflow-auto"
                  >
                    <div className="p-2">
                      <button type="button" onClick={() => { setProgramSearchQuery(''); handleFilterChange('program', ''); setProgramDropdownOpen(false); }} className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px] text-[#617289]">close</span>
                        <span>{t('allPrograms')}</span>
                      </button>
                      {filteredProgramOptions.length > 0 ? filteredProgramOptions.map((prog) => (
                        <button key={prog} type="button" onClick={() => { setProgramSearchQuery(''); handleFilterChange('program', prog); setProgramDropdownOpen(false); }} className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2">
                          <span className="material-symbols-outlined text-[18px] text-[#617289]">folder</span>
                          <span>{prog}</span>
                        </button>
                      )) : (
                        <div className="p-3 text-center text-sm text-[#617289]">{programSearchQuery ? t('noMatchingPrograms') : t('noProgramsAvailable')}</div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#617289]">{t('projectType')}</span>
            <div className="relative" ref={projectTypeDropdownRef}>
              <div className="relative">
                <input
                  type="text"
                  value={projectTypeDropdownOpen ? projectTypeSearchQuery : (filters.project_type || '')}
                  onChange={(e) => {
                    setProjectTypeSearchQuery(e.target.value);
                    if (!e.target.value) handleFilterChange('project_type', '');
                    setProjectTypeDropdownOpen(true);
                  }}
                  onFocus={() => setProjectTypeDropdownOpen(true)}
                  onBlur={() => {
                    setTimeout(() => {
                      setProjectTypeDropdownOpen(false);
                      if (filters.project_type) setProjectTypeSearchQuery('');
                    }, 200);
                  }}
                  className="h-10 rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 px-4 pr-10 text-sm text-[#111418] dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary w-full placeholder-slate-500 dark:placeholder-slate-400"
                  placeholder={t('searchOrSelectProjectType')}
                />
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-[#617289] text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
              <AnimatePresence>
                {projectTypeDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg border border-[#dbe0e6] dark:border-slate-600 shadow-lg max-h-60 overflow-auto"
                  >
                    <div className="p-2">
                      <button type="button" onClick={() => { setProjectTypeSearchQuery(''); handleFilterChange('project_type', ''); setProjectTypeDropdownOpen(false); }} className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2">
                        <span className="material-symbols-outlined text-[18px] text-[#617289]">close</span>
                        <span>{t('allProjectTypes')}</span>
                      </button>
                      {filteredProjectTypeOptions.length > 0 ? filteredProjectTypeOptions.map((pt) => (
                        <button key={pt} type="button" onClick={() => { setProjectTypeSearchQuery(''); handleFilterChange('project_type', pt); setProjectTypeDropdownOpen(false); }} className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2">
                          <span className="material-symbols-outlined text-[18px] text-[#617289]">category</span>
                          <span>{pt}</span>
                        </button>
                      )) : (
                        <div className="p-3 text-center text-sm text-[#617289]">{projectTypeSearchQuery ? t('noMatchingProjectTypes') : t('noProjectTypesAvailable')}</div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-medium text-[#617289]">{t('operationType')}</span>
            <div className="relative">
              <div className="relative">
                <input
                  type="text"
                  value={(operationSearchQuery ?? '').length > 0 ? (operationSearchQuery ?? '') : (filters.operation ? getSelectedOperationName() : '')}
                  onChange={(e) => {
                    setOperationSearchQuery(e.target.value);
                    if (!e.target.value) {
                      handleFilterChange('operation', '');
                    }
                    setOperationDropdownOpen(true);
                  }}
                  onFocus={() => setOperationDropdownOpen(true)}
                  onBlur={() => {
                    setTimeout(() => {
                      setOperationDropdownOpen(false);
                      if (filters.operation) setOperationSearchQuery('');
                    }, 200);
                  }}
                  disabled={loadingOperations}
                  className="h-10 rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 px-4 pr-10 text-sm text-[#111418] dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary w-full disabled:opacity-50 disabled:cursor-not-allowed placeholder-slate-500 dark:placeholder-slate-400"
                  placeholder={t('searchByCodeOrName')}
                />
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-[#617289] text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
              
              {/* Custom Dropdown */}
              <AnimatePresence>
                {operationDropdownOpen && !loadingOperations && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 rounded-lg border border-[#dbe0e6] dark:border-slate-600 shadow-lg max-h-60 overflow-auto"
                  >
                    {filteredOperations.length > 0 ? (
                      <div className="p-2">
                        <p className="text-xs font-semibold text-[#617289] uppercase tracking-wider px-2 py-1 mb-1">
                          {t('operations')}
                        </p>
                        <button
                          type="button"
                          onClick={() => {
                            setOperationSearchQuery('');
                            handleFilterChange('operation', '');
                            setOperationDropdownOpen(false);
                          }}
                          className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2"
                        >
                          <span className="material-symbols-outlined text-[18px] text-[#617289]">close</span>
                          <span>{t('allOperations')}</span>
                        </button>
                        {filteredOperations.map((operation) => (
                          <button
                            key={operation.id}
                            type="button"
                            onClick={() => {
                              setOperationSearchQuery('');
                              handleFilterChange('operation', operation.id.toString());
                              setOperationDropdownOpen(false);
                            }}
                            className="w-full text-left px-3 py-2 rounded-md text-sm text-[#111418] hover:bg-gray-50 transition-colors flex items-center gap-2"
                          >
                            <span className="material-symbols-outlined text-[18px] text-[#617289]">settings</span>
                            <div className="flex flex-col">
                              <span className="font-medium">
                                {operation.production_line_lp}
                              </span>
                              <span className="text-xs text-[#617289]">{operation.activity}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    ) : (
                      <div className="p-3 text-center text-sm text-[#617289]">
                        {operationSearchQuery ? t('noMatchingOperations') : t('noOperationsAvailable')}
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </label>
          
          {filters.period === 'Custom Range' && (
              <>
                 <label className="flex flex-col gap-1.5">
                   <span className="text-xs font-medium text-[#617289]">{t('fromDate')}</span>
                   <input
                     type="date"
                     className="block w-full rounded-lg border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 text-sm text-[#111418] dark:text-slate-100 focus:border-primary focus:ring-primary h-10 px-3"
                     value={filters.date_from}
                     onChange={(e) => handleFilterChange('date_from', e.target.value)}
                     max={filters.date_to || undefined}
                   />
                 </label>
                 <label className="flex flex-col gap-1.5">
                   <span className="text-xs font-medium text-[#617289]">{t('toDate')}</span>
                   <input
                     type="date"
                     className="block w-full rounded-lg border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 text-sm text-[#111418] dark:text-slate-100 focus:border-primary focus:ring-primary h-10 px-3"
                     value={filters.date_to}
                     onChange={(e) => handleFilterChange('date_to', e.target.value)}
                     min={filters.date_from || undefined}
                   />
                 </label>
               </>
             )}
        </div>
        
        {/* Toggle for including incomplete operations */}

        
      {/* Saved Filters Accordion Section */}
      {savedFilters.length > 0 && (
        <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-sm overflow-hidden mt-3">
          {/* Accordion Header */}
          <button
            onClick={() => setSavedFiltersAccordionOpen(!savedFiltersAccordionOpen)}
            className="w-full flex items-center justify-between p-4 hover:bg-[#f9fafb] transition-colors text-left"
          >
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-[20px] text-primary">bookmark</span>
              <h3 className="text-[#111418] text-sm font-semibold">{t('savedFiltersCount', { count: savedFilters.length })}</h3>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSaveFilterDialogOpen(true);
                }}
                className="flex items-center justify-center gap-2 rounded-lg h-8 px-3 bg-white border border-[#dbe0e6] text-primary text-xs font-semibold hover:bg-[#f9fafb] hover:border-[#cbd5e1] transition-colors"
              >
                <span className="material-symbols-outlined text-[16px]">add</span>
                <span>{t('saveCurrentFilter')}</span>
              </button>
              <span className="material-symbols-outlined text-[20px] text-[#617289]">
                {savedFiltersAccordionOpen ? 'expand_less' : 'expand_more'}
              </span>
            </div>
          </button>
          
          {/* Accordion Content */}
          <AnimatePresence>
            {savedFiltersAccordionOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="px-4 pb-4 border-t border-[#f0f2f4]">
                  <div className="overflow-x-auto pb-2 mt-4" style={{ scrollbarWidth: 'thin' }}>
                    <div className="flex gap-4 min-w-max">
                      {savedFilters.map((savedFilter) => {
                        return (
                          <div
                            key={savedFilter.id}
                            className="relative inline-block group border border-[#e5e7eb] rounded-lg overflow-visible bg-white hover:shadow-sm transition-shadow min-w-[200px] max-w-[200px] flex-shrink-0"
                          >
                            {/* Filter Card Header */}
                            <div className="w-full flex items-center justify-between p-3">
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="flex flex-col flex-1 min-w-0">
                                  <span className="text-sm font-semibold text-[#111418] truncate">
                                    {savedFilter.name}
                                  </span>
                                  <span className="text-xs text-[#617289]">
                                    {t('savedOn', { date: new Date(savedFilter.createdAt).toLocaleDateString(getDateLocale(i18n.language)) })}
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleLoadFilter(savedFilter);
                                  }}
                                  className="p-1.5 rounded hover:bg-blue-50 transition-colors"
                                  title={t('applyFilter')}
                                >
                                  <span className="material-symbols-outlined text-[18px] text-blue-600">play_arrow</span>
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteFilter(savedFilter.id, e);
                                  }}
                                  className="p-1.5 rounded hover:bg-red-50 transition-colors"
                                  title={t('deleteFilter')}
                                >
                                  <span className="material-symbols-outlined text-[18px] text-red-600">delete</span>
                                </button>
                              </div>
                            </div>
                            
                            {/* Tooltip - Filter Details */}
                            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 bottom-full left-1/2 -translate-x-1/2 mb-3 w-auto min-w-[300px] max-w-[500px] transition-all duration-300 ease-out transform group-hover:translate-y-0 translate-y-2 z-50">
                              <div className="relative p-4 bg-white rounded-2xl border border-[#e5e7eb] shadow-[0_0_30px_rgba(0,0,0,0.1)]">
                                <div className="flex items-center gap-3 mb-3">
                                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500/20">
                                    <span className="material-symbols-outlined text-blue-600 text-lg">filter_alt</span>
                                  </div>
                                  <div className="flex flex-col">
                                    <h3 className="text-sm font-semibold text-[#111418]">{savedFilter.name}</h3>
                                    <span className="text-xs text-[#617289]">
                                      {t('savedOn', { date: new Date(savedFilter.createdAt).toLocaleDateString(getDateLocale(i18n.language)) })}
                                    </span>
                                  </div>
                                </div>
                                
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                  <div className="grid grid-cols-2 gap-3 text-sm">
                                    <div className="flex flex-col">
                                      <span className="text-xs font-medium text-[#617289] mb-1">{t('period')}</span>
                                      <span className="text-[#111418]">{getFilterDisplayValue('period', savedFilter.filters.period, dashboardData?.filter_options)}</span>
                                    </div>
                                    
                                    {savedFilter.filters.date_from && savedFilter.filters.date_to && (
                                      <>
                                        <div className="flex flex-col">
                                          <span className="text-xs font-medium text-[#617289] mb-1">{t('fromDate')}</span>
                                          <span className="text-[#111418]">{getFilterDisplayValue('date_from', savedFilter.filters.date_from, dashboardData?.filter_options)}</span>
                                        </div>
                                        <div className="flex flex-col">
                                          <span className="text-xs font-medium text-[#617289] mb-1">{t('toDate')}</span>
                                          <span className="text-[#111418]">{getFilterDisplayValue('date_to', savedFilter.filters.date_to, dashboardData?.filter_options)}</span>
                                        </div>
                                      </>
                                    )}
                                    
                                    {savedFilter.filters.centre && (
                                      <div className="flex flex-col">
                                        <span className="text-xs font-medium text-[#617289] mb-1">{t('centre')}</span>
                                        <span className="text-[#111418]">{getFilterDisplayValue('centre', savedFilter.filters.centre, dashboardData?.filter_options)}</span>
                                      </div>
                                    )}
                                    
                                    {savedFilter.filters.team && (
                                      <div className="flex flex-col">
                                        <span className="text-xs font-medium text-[#617289] mb-1">{t('team')}</span>
                                        <span className="text-[#111418]">{getFilterDisplayValue('team', savedFilter.filters.team, dashboardData?.filter_options)}</span>
                                      </div>
                                    )}
                                    
                                    {savedFilter.filters.employee && (
                                      <div className="flex flex-col">
                                        <span className="text-xs font-medium text-[#617289] mb-1">{t('employee')}</span>
                                        <span className="text-[#111418]">{getFilterDisplayValue('employee', savedFilter.filters.employee, dashboardData?.filter_options)}</span>
                                      </div>
                                    )}
                                    
                                    {((Array.isArray(savedFilter.filters.domain) && savedFilter.filters.domain.length > 0) || savedFilter.filters.domain) && (
                                      <div className="flex flex-col">
                                        <span className="text-xs font-medium text-[#617289] mb-1">{t('domain')}</span>
                                        <span className="text-[#111418]">{getFilterDisplayValue('domain', savedFilter.filters.domain, dashboardData?.filter_options)}</span>
                                      </div>
                                    )}
                                    
                                    {savedFilter.filters.operation && (
                                      <div className="flex flex-col">
                                        <span className="text-xs font-medium text-[#617289] mb-1">{t('operation')}</span>
                                        <span className="text-[#111418]">{getFilterDisplayValue('operation', savedFilter.filters.operation, dashboardData?.filter_options, operationsList)}</span>
                                      </div>
                                    )}
                                    
                                    {savedFilter.filters.program && (
                                      <div className="flex flex-col">
                                        <span className="text-xs font-medium text-[#617289] mb-1">{t('program')}</span>
                                        <span className="text-[#111418]">{savedFilter.filters.program}</span>
                                      </div>
                                    )}
                                    
                                    {savedFilter.filters.project_type && (
                                      <div className="flex flex-col">
                                        <span className="text-xs font-medium text-[#617289] mb-1">{t('projectType')}</span>
                                        <span className="text-[#111418]">{savedFilter.filters.project_type}</span>
                                      </div>
                                    )}
                                    
                                    {Array.isArray(savedFilter.filters.weekdays) && savedFilter.filters.weekdays.length > 0 && (
                                      <div className="flex flex-col">
                                        <span className="text-xs font-medium text-[#617289] mb-1">{t('days')}</span>
                                        <span className="text-[#111418]">
                                          {WEEKDAY_TOGGLE_CONFIG.filter((c) => savedFilter.filters.weekdays.includes(c.day))
                                            .map((c) => t(c.labelKey))
                                            .join(', ')}
                                        </span>
                                      </div>
                                    )}
                                    
                                    <div className="flex flex-col">
                                      <span className="text-xs font-medium text-[#617289] mb-1">{t('includeIncomplete')}</span>
                                      <span className="text-[#111418]">{getFilterDisplayValue('includeIncompleteOperations', savedFilter.filters.includeIncompleteOperations, dashboardData?.filter_options)}</span>
                                    </div>
                                    
                                    {savedFilter.filters.includeIncompleteOperations && (
                                      <div className="flex flex-col">
                                        <span className="text-xs font-medium text-[#617289] mb-1">{t('showAllStatusesShort')}</span>
                                        <span className="text-[#111418]">{getFilterDisplayValue('showAllStatuses', savedFilter.filters.showAllStatuses, dashboardData?.filter_options)}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 border-r border-b border-[#e5e7eb]"></div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
      
      <div className="flex flex-col gap-4 lg:flex-row lg:gap-6 lg:items-end lg:justify-between mt-3">
        <div className="flex gap-3 w-full lg:w-auto flex-wrap">
          {savedFilters.length === 0 && (
            <button
              onClick={() => setSaveFilterDialogOpen(true)}
              className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-white border border-[#dbe0e6] text-primary text-sm font-semibold hover:bg-[#f9fafb] hover:border-[#cbd5e1] transition-colors whitespace-nowrap"
            >
              <span className="material-symbols-outlined text-[20px]">save</span>
              <span>{t('saveFilter')}</span>
            </button>
          )}
          
        <button
            onClick={handleClearFilters}
            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-white border border-[#dbe0e6] text-primary text-sm font-semibold hover:bg-[#f9fafb] hover:border-[#cbd5e1] transition-colors whitespace-nowrap"
          >
            <span className="material-symbols-outlined text-[20px]">clear_all</span>
            <span>{t('clearFilters')}</span>
          </button>
          <button
            onClick={handleRefreshData}
            disabled={loading}
            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-white border border-[#dbe0e6] text-primary text-sm font-semibold hover:bg-[#f9fafb] hover:border-[#cbd5e1] transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className={`material-symbols-outlined text-[20px] ${loading ? 'animate-spin' : ''}`}>refresh</span>
            <span>{tCommon('refresh')}</span>
          </button>
        </div>
        <div
          className="flex flex-wrap items-center gap-1 justify-center w-full lg:flex-1 lg:min-w-0 lg:max-w-xl lg:px-2"
          role="group"
          aria-label={t('filterByWeekday')}
        >
          {WEEKDAY_TOGGLE_CONFIG.map((w, idx) => (
            <button
              key={`wd-${w.day}-${idx}`}
              type="button"
              title={t(w.labelKey)}
              onClick={() => toggleWeekday(w.day)}
              className={`min-w-[30px] h-8 rounded-md text-xs font-bold border transition-colors ${
                isWeekdayActive(w.day)
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-[#94a3b8] border-[#dbe0e6] dark:bg-slate-800 dark:border-slate-600 dark:text-slate-400'
              }`}
            >
              {w.letter}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-center gap-3 w-full lg:w-auto">             
          <span className="text-sm font-medium text-gray">
            {exporting ? t('preparingExportLong') : t('exportAs')}
          </span>     
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting}
            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors flex-1 lg:flex-none whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">download</span>
            <span>{t('csv')}</span>
          </button>
          <button
            onClick={() => handleExport('excel')}
            disabled={exporting}
            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold hover:bg-primary-hover transition-colors flex-1 lg:flex-none whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className="material-symbols-outlined text-[20px]">file_save</span>
            <span>{t('excel')}</span>
          </button>
        </div>
      </div>
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Card 1: Total Operations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white p-5 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col justify-between h-32"
        >
          <div className="flex justify-between items-start">
            <p className="text-[#617289] text-sm font-medium">{t('totalOperations')}</p>
            <div className="bg-blue-50 dark:bg-blue-900/40 p-1.5 rounded-md text-primary dark:text-blue-300">
              <span className="material-symbols-outlined text-[20px]">dataset</span>
            </div>
          </div>
          <div>
            <p className="text-[#111418] text-3xl font-black tracking-tight">
              {kpis?.total_operations?.toLocaleString() || 0}
            </p>
          </div>
        </motion.div>
        
        {/* Card 3: Number of Domains */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          className="bg-white p-5 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col justify-between h-32"
        >
          <div className="flex justify-between items-start">
            <p className="text-[#617289] text-sm font-medium">{t('totalDomains')}</p>
            <div className="bg-purple-50 dark:bg-purple-900/40 p-1.5 rounded-md text-purple-600 dark:text-purple-300">
              <span className="material-symbols-outlined text-[20px]">category</span>
            </div>
          </div>
          <div>
            <p className="text-[#111418] text-3xl font-black tracking-tight">
              {domain_distribution?.length || 0}
            </p>
          </div>
        </motion.div>
        
        {/* Card 2: Theoretical Time */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white p-5 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col justify-between h-32"
        >
          <div className="flex justify-between items-start">
            <p className="text-[#617289] text-sm font-medium">{t('standardTime')}</p>
            <div className="bg-indigo-50 dark:bg-indigo-900/40 p-1.5 rounded-md text-indigo-600 dark:text-indigo-300">
              <span className="material-symbols-outlined text-[20px]">schedule</span>
            </div>
          </div>
          <div>
            <p className="text-[#111418] text-3xl font-black tracking-tight">
              {kpis?.theoretical_time != null ? formatHoursAndMinutes(kpis.theoretical_time) : t('hrsMin', { h: 0, m: 0 })}
            </p>
          </div>
        </motion.div>
        
        {/* Card 4: Efficiency Rate = (total Standard Time imputed × 100) ÷ (total employees × 8) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.3 }}
          className="bg-white p-5 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col justify-between min-h-[8rem]"
        >
          <div className="flex justify-between items-start">
            <p className="text-[#617289] text-sm font-medium">{t('efficiencyRate')}</p>
            <div className="bg-emerald-50 dark:bg-emerald-900/40 p-1.5 rounded-md text-emerald-600 dark:text-emerald-300">
              <span className="material-symbols-outlined text-[20px]">speed</span>
            </div>
          </div>
          <div>
            <p className="text-[#111418] text-3xl font-black tracking-tight">
              {(() => {
                // Backend: efficiency_rate = (total Standard Time imputed × 100) ÷ (total employees platform × 8 × days)
                const efficiencyRate = kpis?.efficiency_rate;
                
                // #region agent log
                // fetch('http://127.0.0.1:7247/ingest/69caa0a3-7f19-4041-86d8-9ea723023a00',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'ManagerDashboardView.js:EfficiencyRate',message:'using backend efficiency_rate',data:{efficiencyRate,hasKpis:!!kpis,totalOps:kpis?.total_operations},timestamp:Date.now(),sessionId:'debug-session',runId:'post-fix',hypothesisId:'I'})}).catch(()=>{});
                // #endregion
                
                if (efficiencyRate === undefined || efficiencyRate === null) {
                  return '0.00';
                }
                
                return efficiencyRate.toFixed(2);
              })()}%
            </p>

            {/* <p className="text-[#617289] text-xs mt-1.5 leading-tight">
              ({kpis?.total_standard_time_imputed ?? kpis?.theoretical_time ?? 0} × 100) ÷ ({(kpis?.employees_for_efficiency_denominator ?? kpis?.total_employees_platform) ?? 0} × 8 × {kpis?.days_in_period ?? 1})
            </p> */}
          </div>
        </motion.div>
      </div>
      
      {/* Operations Overview Charts - One row on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Chart 1: Executed Operations Overview - Takes 2/3 width on large screens */}
        <div className="bg-white p-6 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col lg:col-span-2">
        <div className="flex items-center justify-between mb-6">
            <h3 className="text-[#111418] text-lg font-bold">{t('executedOpsOverview')}</h3>
            <div className="flex items-center gap-4 flex-wrap">
              {/* View toggle */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-[#617289] font-medium">{t('viewLabel')}</span>
                <button
                  onClick={() => setExecutedOpsViewByDomain(false)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    !executedOpsViewByDomain
                      ? 'bg-primary text-white'
                      : 'bg-[#f9fafb] text-[#617289] hover:bg-[#f0f2f4]'
                  }`}
                >
                  {t('viewLinear')}
                </button>
                <button
                  onClick={() => setExecutedOpsViewByDomain(true)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                    executedOpsViewByDomain
                      ? 'bg-primary text-white'
                      : 'bg-[#f9fafb] text-[#617289] hover:bg-[#f0f2f4]'
                  }`}
                >
                  {t('viewBar')}
                </button>
              </div>
            </div>
          </div>
          
              {/* Metric tabs: full width with animated sliding indicator */}
              <div className="w-full mb-6">
                <nav className="relative flex w-full border-b border-[#e5e7eb]">
                  <button
                    onClick={() => setOperationsMetric('count')}
                    className={`flex-1 px-4 py-2.5 font-medium text-sm transition-colors ${
                      operationsMetric === 'count'
                        ? 'text-primary'
                        : 'text-[#617289] hover:text-[#111418]'
                    }`}
                  >
                    {t('operations')}
                  </button>
                  <button
                    onClick={() => setOperationsMetric('standard')}
                    className={`flex-1 px-4 py-2.5 font-medium text-sm transition-colors ${
                      operationsMetric === 'standard'
                        ? 'text-primary'
                        : 'text-[#617289] hover:text-[#111418]'
                    }`}
                  >
                    {t('standardHours')}
                  </button>
                  <motion.div
                    className="absolute bottom-0 h-0.5 bg-primary"
                    style={{ width: '50%' }}
                    initial={false}
                    animate={{
                      left: operationsMetric === 'count' ? '0%' : '50%',
                    }}
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                </nav>
              </div>
          <div className="flex-1 relative overflow-hidden" style={{ minHeight: '240px', maxHeight: '360px' }}>
            {operationsVolumeChartData ? (
              <AnimatePresence mode="wait">
                {executedOpsViewByDomain ? (
                  <motion.div
                    key="bar-chart"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="absolute inset-0 w-full h-full"
                  >
                    <Bar data={operationsVolumeChartData} options={operationsVolumeChartOptions} />
                  </motion.div>
                ) : (
                  <motion.div
                    key="line-chart"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                    className="absolute inset-0 w-full h-full"
                  >
                    <Line data={operationsVolumeChartData} options={operationsVolumeChartOptions} />
                  </motion.div>
                )}
              </AnimatePresence>
            ) : (
              <div className="flex items-center justify-center h-full text-center text-[#617289]">
                {t('noOperationsData')}
              </div>
            )}
          </div>
          {/* Domain Legend for Bar Chart - Only show when displaying domains, not when showing completed/incomplete */}
          {executedOpsViewByDomain && operationsVolumeDomains.length > 0 && operationsVolumeChartData?.datasets?.length !== 2 && (
            <div className="mt-6 pt-6 border-t border-[#f0f2f4]">
              <div className="flex flex-wrap gap-4 justify-center">
                {operationsVolumeDomains.map((domain, index) => {
                  const colorPalette = isRsDashboard
                    ? RS_CHART_SERIES
                    : ['#1162d4', '#60a5fa', '#93c5fd', '#cbd5e1', '#fbbf24', '#f59e0b', '#ef4444', '#10b981', '#8b5cf6', '#ec4899'];
                  const color = colorPalette[index % colorPalette.length];
                  return (
                    <div key={index} className="flex items-center gap-2 text-sm">
                      <div className="size-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }}></div>
                      <span className="text-[#111418]">{domain}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Chart 2: Distribution by Domain or Production Type - Takes 1/3 width on large screens */}
        <div className="bg-white p-4 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col">
          <h3 className="text-[#111418] dark:text-slate-100 text-lg font-bold mb-4">{t('operationsDistributedBy')} </h3>
          <div className="w-full mb-4">
            <nav className="relative flex w-full border-b border-[#e5e7eb] dark:border-slate-600">
              <button
                type="button"
                onClick={() => setDistributionChartMode('domain')}
                className={`flex-1 min-w-0 px-2 py-2.5 font-medium text-xs sm:text-sm transition-colors ${
                  distributionChartMode === 'domain' ? 'text-primary' : 'text-[#617289] dark:text-slate-400 hover:text-[#111418] dark:hover:text-slate-100'
                }`}
              >
                {t('domain')}
              </button>
              <button
                type="button"
                onClick={() => setDistributionChartMode('production_type')}
                className={`flex-1 min-w-0 px-2 py-2.5 font-medium text-xs sm:text-sm transition-colors ${
                  distributionChartMode === 'production_type' ? 'text-primary' : 'text-[#617289] dark:text-slate-400 hover:text-[#111418] dark:hover:text-slate-100'
                }`}
              >
                {t('productionTypeLabel')}
              </button>
              <motion.div
                className="absolute bottom-0 h-0.5 bg-primary"
                style={{ width: '50%' }}
                initial={false}
                animate={{ left: distributionChartMode === 'domain' ? '0%' : '50%' }}
                transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              />
            </nav>
          </div>
          <div className="flex items-center justify-center py-4 flex-1" style={{ minHeight: '210px' }}>
            {domainOperationsChartData && domainOperationsChartData.innerData.length > 0 ? (
              <div className="relative w-full max-w-[192px] h-[192px] flex items-center justify-center">
                {/* Domain Distribution Donut Chart */}
                <Doughnut
                  data={{
                    labels: domainOperationsChartData.innerLabels,
                    datasets: [{
                      data: domainOperationsChartData.innerData,
                      backgroundColor: domainOperationsChartData.innerColors,
                      borderColor: '#fff',
                      borderWidth: 2,
                    }],
                  }}
                  options={{
                    ...domainOperationsInnerChartOptions,
                    cutout: '60%', // Standard donut cutout
                  }}
                />
                {/* Center text */}
                <div className="absolute inset-0 flex items-center justify-center flex-col pointer-events-none z-10">
                  <span className="text-lg font-bold text-[#111418]">
                    {activeDistributionList?.length || 0} {distributionChartMode === 'domain' ? t('domains') : t('productionTypes')}
                  </span>
                  <span className="text-xs text-[#617289]">
                    {filters.includeIncompleteOperations 
                      ? t('totalOpsCount', { count: activeDistributionList?.reduce((sum, item) => sum + (item.count || 0), 0) || 0 })
                      : t('totalOps')}
                  </span>
                  {filters.includeIncompleteOperations && activeDistributionList && activeDistributionList.length > 0 && (
                    filters.showAllStatuses ? (
                      <div className="flex flex-wrap items-center justify-center gap-1.5 mt-1 text-[10px]">
                        {Object.entries(statusBreakdownLegendMap).map(([status, { label, color }]) => {
                          const total = activeDistributionList.reduce((sum, item) => 
                            sum + ((item.status_breakdown && item.status_breakdown[status]) || 0), 0);
                          if (total === 0) return null;
                          return (
                            <span key={status} style={{ color }}>
                              {total} {label.toLowerCase()}
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-1 text-[10px]">
                        <span className={isRsDashboard ? 'text-primary' : 'text-[#1162d4]'}>
                          {t('completedCount', { count: activeDistributionList.reduce((sum, item) => sum + (item.completed_count || 0), 0) })}
                        </span>
                        <span className="text-[#6b7280]">
                          {t('incompleteCount', { count: activeDistributionList.reduce((sum, item) => sum + (item.incomplete_count || 0), 0) })}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center text-[#617289]">{t('noData')}</div>
            )}
          </div>
          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-[#f0f2f4]">
            {/* Groups Legend (Domains or Production Types) - Tooltips */}
            <div className="mb-4">
              <p className="text-xs font-semibold text-[#617289] uppercase tracking-wider mb-3">
                {distributionChartMode === 'domain' ? t('domains') : t('productionTypes')}
              </p>
              <div className="flex flex-col gap-2">
                {activeDistributionList?.slice(0, 4).map((item, index) => {
                  const groupLabel = distributionChartMode === 'domain' ? (item.domain || t('other')) : (item.production_type || t('other'));
                  const colors = isRsDashboard ? RS_CHART_GROUP : ['#1162d4', '#60a5fa', '#93c5fd', '#e2e8f0'];
                  const color = colors[index % colors.length];
                  const domainOps = domainOperationsChartData?.operationMap?.[groupLabel] || [];
                  
                  return (
                    <div key={index} className="relative inline-block group">
                      <div className="flex items-center justify-between p-3 rounded-lg hover:bg-[#f9fafb] transition-colors">
                        <div className="flex items-center gap-2 flex-1">
                          <div className={`size-3 rounded-full flex-shrink-0`} style={{ backgroundColor: color }}></div>
                          <span className="text-sm font-medium text-[#111418] text-left">
                            {groupLabel}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const itemId = `domain-label-${index}`;
                              handleCopy(groupLabel, itemId);
                            }}
                            className="flex-shrink-0 p-1 rounded hover:bg-blue-50 transition-colors"
                            title={distributionChartMode === 'domain' ? t('copyDomainName') : t('copyProductionType')}
                          >
                            <span className={`material-symbols-outlined text-sm ${copiedItem === `domain-label-${index}` ? 'text-blue-600' : 'text-[#617289]'}`}>
                              {copiedItem === `domain-label-${index}` ? 'check' : 'content_copy'}
                            </span>
                          </button>
                          <span className="text-xs text-[#617289]">
                            ({item.percentage || 0}%)
                          </span>
                          {filters.includeIncompleteOperations && (() => {
                            if (filters.showAllStatuses && item.status_breakdown) {
                              return (
                                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                  {Object.entries(statusBreakdownLegendMap).map(([status, { label, color }], idx, arr) => {
                                    const count = item.status_breakdown[status] || 0;
                                    if (count === 0) return null;
                                    const hasMore = arr.slice(idx + 1).some(([s]) => (item.status_breakdown[s] || 0) > 0);
                                    return (
                                      <span key={status} className="font-medium" style={{ color }}>
                                        {count} {label.toLowerCase()}
                                        {hasMore && <span className="text-[#617289] ml-1">•</span>}
                                      </span>
                                    );
                                  })}
                                </div>
                              );
                            } else if (item.completed_count !== undefined || item.incomplete_count !== undefined) {
                              return (
                                <div className="flex items-center gap-1.5 text-xs">
                                  <span className={`font-medium ${isRsDashboard ? 'text-primary' : 'text-[#1162d4]'}`}>
                                    {t('completedCount', { count: item.completed_count || 0 })}
                                  </span>
                                  {item.incomplete_count > 0 && (
                                    <>
                                      <span className="text-[#617289]">•</span>
                                      <span className="text-[#6b7280] font-medium">
                                        {t('incompleteCount', { count: item.incomplete_count || 0 })}
                                      </span>
                                    </>
                                  )}
                                </div>
                              );
                            }
                            return null;
                          })()}
                          {domainOps.length > 0 && (
                            <span className="text-xs text-[#617289] bg-[#f0f2f4] px-2 py-0.5 rounded-full">
                              {t('operationCount', { count: domainOps.length })}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Tooltip - Operations */}
                      {domainOps.length > 0 && (
                        <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 bottom-full left-1/2 -translate-x-1/2 mb-3 w-auto min-w-[300px] max-w-[500px] transition-all duration-300 ease-out transform group-hover:translate-y-0 translate-y-2 z-50">
                          <div className="relative p-4 bg-white rounded-2xl border border-[#e5e7eb] shadow-[0_0_30px_rgba(0,0,0,0.1)]">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500/20">
                                <span className="material-symbols-outlined text-blue-600 text-lg">work</span>
                              </div>
                              <div className="flex flex-col">
                                <h3 className="text-sm font-semibold text-[#111418]">{groupLabel}</h3>
                                <div className="flex items-center gap-2 text-xs">
                                  <span className="text-[#617289]">({item.percentage || 0}%)</span>
                                  {filters.includeIncompleteOperations && (() => {
                                    if (filters.showAllStatuses && item.status_breakdown) {
                                      return (
                                        <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                          {Object.entries(statusBreakdownLegendMap).map(([status, { label, color }], idx, arr) => {
                                            const count = item.status_breakdown[status] || 0;
                                            if (count === 0) return null;
                                            const hasMore = arr.slice(idx + 1).some(([s]) => (item.status_breakdown[s] || 0) > 0);
                                            return (
                                              <span key={status} className="font-medium" style={{ color }}>
                                                {count} {label.toLowerCase()}
                                                {hasMore && <span className="text-[#617289] ml-1">•</span>}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      );
                                    } else if (item.completed_count !== undefined || item.incomplete_count !== undefined) {
                                      return (
                                        <>
                                          <span className={`font-medium ${isRsDashboard ? 'text-primary' : 'text-[#1162d4]'}`}>
                                            {t('completedCount', { count: item.completed_count || 0 })}
                                          </span>
                                          {item.incomplete_count > 0 && (
                                            <>
                                              <span className="text-[#617289]">•</span>
                                              <span className="text-[#6b7280] font-medium">
                                                {t('incompleteCount', { count: item.incomplete_count || 0 })}
                                              </span>
                                            </>
                                          )}
                                        </>
                                      );
                                    }
                                    return null;
                                  })()}
                                </div>
                              </div>
                            </div>
                            
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {domainOps.map((op, opIndex) => {
                                const itemId = `domain-op-${opIndex}`;
                                const isCopied = copiedItem === itemId;
                                const hasCompletionStatus = op.completed_count !== undefined || op.incomplete_count !== undefined;
                                return (
                                  <div key={opIndex} className="flex items-start gap-2 p-2 rounded-lg hover:bg-[#f9fafb] transition-colors">
                                    <div className="size-2 rounded-full flex-shrink-0 hidden mt-1.5" style={{ backgroundColor: op.color }}></div>
                                    <div className="flex flex-col flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-[#111418] truncate">
                                          {op.activity || t('unknownOperation')}
                                        </span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(op.activity || t('unknownOperation'), itemId);
                                          }}
                                          className="flex-shrink-0 p-1 rounded hover:bg-blue-50 transition-colors"
                                          title={t("copyName")}
                                        >
                                          <span className={`material-symbols-outlined text-sm ${isCopied ? 'text-blue-600' : 'text-[#617289]'}`}>
                                            {isCopied ? 'check' : 'content_copy'}
                                          </span>
                                        </button>
                                      </div>
                                      {hasCompletionStatus && filters.includeIncompleteOperations && (
                                        <div className="flex items-center gap-2 mt-1 text-xs">
                                          <span className={isRsDashboard ? 'text-primary' : 'text-[#1162d4]'}>
                                            {t('completedCount', { count: op.completed_count || 0 })}
                                          </span>
                                          {op.incomplete_count > 0 && (
                                            <>
                                              <span className="text-[#617289]">•</span>
                                              <span className="text-[#6b7280]">
                                                {t('incompleteCount', { count: op.incomplete_count || 0 })}
                                              </span>
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                      <span className="text-xs font-semibold text-blue-600 flex-shrink-0">
                                        {op.count}
                                      </span>
                                      {hasCompletionStatus && filters.includeIncompleteOperations && op.incomplete_count > 0 && (
                                        <span className="text-[10px] text-[#6b7280]">
                                          {t('totalLower')}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            
                            <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 border-r border-b border-[#e5e7eb]"></div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Occupation rates chart - full-width row */}
      <div className="w-full mt-4">
        <OccupationRatesChartCard
          data={dashboardData?.occupation_rates_series}
          filters={filters}
          loading={loading}
          formatChartLabel={formatChartLabel}
        />
      </div>
      
      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Chart 1: Aggregated Top Operations - Takes 3/5 width on large screens */}
        <div className="bg-white p-4 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col lg:col-span-3">
          <h3 className="text-[#111418] text-lg font-bold mb-4">{t('topOperations')}</h3>
          <div className="flex-1" style={{ minHeight: '240px', maxHeight: '360px' }}>
            {topOperationsChartData ? (
              <Bar data={topOperationsChartData} options={topOperationsChartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full text-center text-[#617289]">
                {t('noOperationsData')}
              </div>
            )}
          </div>
          {/* Label List for Operations with Tooltips */}
          {topOperationsChartResult && topOperationsChartResult.labels && topOperationsChartResult.labels.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#f0f2f4]">
              <ul className="flex flex-col gap-2">
                {topOperationsChartResult.labels.map((label, index) => {
                  const operation = topOperationsChartResult.operationData?.[index];
                  const fullOperation = top_operations?.[index];
                  const employees = fullOperation?.employees || [];
                  
                  return (
                    <li key={index} className="relative inline-block group">
                      <div className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-[#f9fafb] transition-colors">
                        <div className="flex items-center justify-center min-w-[28px] h-7 rounded-md bg-[#f9fafb] border border-[#e5e7eb] text-[#617289] font-semibold text-xs">
                          {index + 1}
          </div>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="size-2 rounded-full flex-shrink-0 hidden" style={{ backgroundColor: isRsDashboard ? RS_CHART_BASE : '#1162d4' }}></div>
                          <span className="text-[#111418] text-sm truncate" title={operation?.name || label}>
                            {operation?.name || label}
                          </span>
                          <button
                            onClick={(e) => {
                      e.stopPropagation();
                              const itemId = `op-label-${index}`;
                              handleCopy(operation?.name || label, itemId);
                            }}
                            className="flex-shrink-0 p-1 rounded hover:bg-blue-50 transition-colors"
                            title={t("copyName")}
                          >
                            <span className={`material-symbols-outlined text-sm ${copiedItem === `op-label-${index}` ? 'text-blue-600' : 'text-[#617289]'}`}>
                              {copiedItem === `op-label-${index}` ? 'check' : 'content_copy'}
                            </span>
                          </button>
                        </div>
                        {employees.length > 0 && (
                          <span className="text-xs text-[#617289] bg-[#f0f2f4] px-2 py-0.5 rounded-full flex-shrink-0">
                            {t('employeeCount', { count: employees.length })}
                          </span>
                        )}
                      </div>
        
                      {/* Tooltip - Employees */}
                      {employees.length > 0 && (
                        <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 bottom-full left-1/2 -translate-x-1/2 mb-3 w-auto min-w-[300px] max-w-[500px] transition-all duration-300 ease-out transform group-hover:translate-y-0 translate-y-2 z-50">
                          <div className="relative p-4 bg-white rounded-2xl border border-[#e5e7eb] shadow-[0_0_30px_rgba(0,0,0,0.1)]">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500/20">
                                <span className="material-symbols-outlined text-blue-600 text-lg">people</span>
                              </div>
                              <h3 className="text-sm font-semibold text-[#111418]">{t('employees')}</h3>
                            </div>
                            
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {employees.map((emp, empIndex) => {
                                const itemId = `emp-${emp.id || empIndex}`;
                                const isCopied = copiedItem === itemId;
                    return (
                                  <div key={emp.id || empIndex} className="flex items-start gap-2 p-2 rounded-lg hover:bg-[#f9fafb] transition-colors">
                                    <div className="size-2 rounded-full flex-shrink-0 hidden mt-1.5" style={{ backgroundColor: isRsDashboard ? RS_CHART_DUAL_BAR.secondary : '#3b82f6' }}></div>
                                    <div className="flex flex-col flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-[#111418] truncate">
                                          {emp.name || t('unknown')}
                                        </span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(emp.name || t('unknown'), itemId);
                                          }}
                                          className="flex-shrink-0 p-1 rounded hover:bg-blue-50 transition-colors"
                                          title={t("copyName")}
                                        >
                                          <span className={`material-symbols-outlined text-sm ${isCopied ? 'text-blue-600' : 'text-[#617289]'}`}>
                                            {isCopied ? 'check' : 'content_copy'}
                                          </span>
                                        </button>
                                      </div>
                                      <span className="text-xs text-[#617289] truncate">
                          {emp.email || '-'}
                                      </span>
                                    </div>
                                    <span className="text-xs font-semibold text-blue-600 flex-shrink-0">
                                      {t('opCount', { count: emp.operation_count || 0 })}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            
                            <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 border-r border-b border-[#e5e7eb]"></div>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
        
        {/* Chart 2: Top Performing Employees - Takes 2/5 width on large screens */}
        <div className="bg-white p-4 rounded-xl border border-[#e5e7eb] shadow-sm flex flex-col lg:col-span-2">
          <h3 className="text-[#111418] text-lg font-bold mb-4">{t('topEmployees')}</h3>
          <div className="flex-1" style={{ minHeight: '240px', maxHeight: '360px' }}>
            {topEmployeesChartData ? (
              <Bar data={topEmployeesChartData} options={topEmployeesChartOptions} />
            ) : (
              <div className="flex items-center justify-center h-full text-center text-[#617289]">
                {t('noEmployeesData')}
              </div>
            )}
          </div>
          {/* Label List for Employees with Tooltips */}
          {topEmployeesChartResult && topEmployeesChartResult.labels && topEmployeesChartResult.labels.length > 0 && (
            <div className="mt-4 pt-4 border-t border-[#f0f2f4]">
              <ul className="flex flex-col gap-2">
                {topEmployeesChartResult.labels.map((label, index) => {
                  const employee = topEmployeesChartResult.employeeData?.[index];
                  const fullEmployee = top_employees?.[index];
                  const operations = fullEmployee?.operations || [];
                  
                  return (
                    <li key={index} className="relative inline-block group">
                      <div className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-[#f9fafb] transition-colors">
                        <div className="flex items-center justify-center min-w-[28px] h-7 rounded-md bg-[#f9fafb] border border-[#e5e7eb] text-[#617289] font-semibold text-xs">
                          {index + 1}
        </div>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <div className="size-2 rounded-full flex-shrink-0 hidden" style={{ backgroundColor: isRsDashboard ? RS_CHART_BASE : '#1162d4' }}></div>
                          <span className="text-[#111418] text-sm truncate" title={employee?.name || label}>
                            {employee?.name || label}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const itemId = `emp-label-${index}`;
                              handleCopy(employee?.name || label, itemId);
                            }}
                            className="flex-shrink-0 p-1 rounded hover:bg-blue-50 transition-colors"
                            title={t("copyName")}
                          >
                            <span className={`material-symbols-outlined text-sm ${copiedItem === `emp-label-${index}` ? 'text-blue-600' : 'text-[#617289]'}`}>
                              {copiedItem === `emp-label-${index}` ? 'check' : 'content_copy'}
                            </span>
                          </button>
                        </div>
                        {operations.length > 0 && (
                          <span className="text-xs text-[#617289] bg-[#f0f2f4] px-2 py-0.5 rounded-full flex-shrink-0">
                            {t('operationCount', { count: operations.length })}
                          </span>
                        )}
                      </div>
                      
                      {/* Tooltip - Operations */}
                      {operations.length > 0 && (
                        <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 bottom-full left-1/2 -translate-x-1/2 mb-3 w-auto min-w-[300px] max-w-[500px] transition-all duration-300 ease-out transform group-hover:translate-y-0 translate-y-2 z-50">
                          <div className="relative p-4 bg-white rounded-2xl border border-[#e5e7eb] shadow-[0_0_30px_rgba(0,0,0,0.1)]">
                            <div className="flex items-center gap-3 mb-2">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-500/20">
                                <span className="material-symbols-outlined text-blue-600 text-lg">work</span>
                              </div>
                              <h3 className="text-sm font-semibold text-[#111418]">{t('operations')}</h3>
                            </div>
                            
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {operations.map((op, opIndex) => {
                                const itemId = `op-${op.id || opIndex}`;
                                const isCopied = copiedItem === itemId;
                                return (
                                  <div key={op.id || opIndex} className="flex items-start gap-2 p-2 rounded-lg hover:bg-[#f9fafb] transition-colors">
                                    <div className="size-2 rounded-full flex-shrink-0 hidden mt-1.5" style={{ backgroundColor: isRsDashboard ? RS_CHART_DUAL_BAR.secondary : '#3b82f6' }}></div>
                                    <div className="flex flex-col flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-[#111418] truncate">
                                          {op.activity || t('unknownOperation')}
                                        </span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleCopy(op.activity || t('unknownOperation'), itemId);
                                          }}
                                          className="flex-shrink-0 p-1 rounded hover:bg-blue-50 transition-colors"
                                          title={t("copyName")}
                                        >
                                          <span className={`material-symbols-outlined text-sm ${isCopied ? 'text-blue-600' : 'text-[#617289]'}`}>
                                            {isCopied ? 'check' : 'content_copy'}
                                          </span>
                                        </button>
                                      </div>
                                      <span className="text-xs text-[#617289] truncate">
                                        {op.domain || t('other')} • {op.production_line_lp || '-'}
                                      </span>
                                    </div>
                                    <span className="text-xs font-semibold text-blue-600 flex-shrink-0">
                                      {op.operation_count || 0} {op.operation_count === 1 ? 'time' : 'times'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                            
                            <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-white rotate-45 border-r border-b border-[#e5e7eb]"></div>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>
      
      {/* Save Filter Dialog */}
      <AnimatePresence>
        {saveFilterDialogOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => {
              setSaveFilterDialogOpen(false);
              setSaveFilterName('');
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl border border-[#e5e7eb] shadow-lg p-6 max-w-md w-full mx-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-[#111418]">{t('saveFilter')}</h3>
                <button
                  onClick={() => {
                    setSaveFilterDialogOpen(false);
                    setSaveFilterName('');
                  }}
                  className="p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <span className="material-symbols-outlined text-[20px] text-[#617289]">close</span>
                </button>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#111418] mb-2">
                  {t('filterName')}
                </label>
                <input
                  type="text"
                  value={saveFilterName}
                  onChange={(e) => setSaveFilterName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveFilter();
                    } else if (e.key === 'Escape') {
                      setSaveFilterDialogOpen(false);
                      setSaveFilterName('');
                    }
                  }}
                  placeholder={t("filterNamePlaceholder")}
                  className="w-full rounded-lg border border-[#dbe0e6] dark:border-slate-600 bg-[#f9fafb] dark:bg-slate-700 px-4 py-2 text-sm text-[#111418] dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary placeholder-slate-500 dark:placeholder-slate-400"
                  autoFocus
                />
              </div>
              
              <div className="flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setSaveFilterDialogOpen(false);
                    setSaveFilterName('');
                  }}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-[#617289] bg-[#f9fafb] hover:bg-[#f0f2f4] transition-colors"
                >
                  {tCommon('cancel')}
                </button>
                <button
                  onClick={handleSaveFilter}
                  disabled={!saveFilterName.trim()}
                  className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-primary hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {tCommon('save')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ManagerDashboardView;

